import React, { useState, useRef, useEffect, useMemo } from 'react';
import ChatMessage from '../../components/ChatMessage';
import MeetingAnalysisModal from '../../components/MeetingAnalysisModal';
import RejectCandidateModal from '../../components/InterviewScheduler/RejectCandidateModal';
import ChatInput from '../../components/ChatInput';
import AgentStageBanner from '../../components/AgentStageBanner';
import JDOverview from '../../components/JDOverview';
import ScoreDetailsInfo from '../../components/ScoreDetailsInfo';
import { useSettings } from '../../contexts/SettingsContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useSyncContext } from '../../contexts/SyncContext';
import { useTimezone } from '../../contexts/TimezoneContext';
import aiAutomationApi from '../../utils/aiAutomationApi';
import '../../styles/chat.css';
import { getSourceTypeLabel } from '../../utils/uxLabels';
import { getMetadata, getCid } from '../../utils/encryptedStorage';
import { FiBriefcase, FiCheck, FiChevronDown, FiPlus, FiSearch, FiSliders } from 'react-icons/fi';

const createInitialMessages = (appName) => {
  const name = appName || 'Wizbox';
  return [
    {
      id: 1,
      sender: 'agent',
      message: "Welcome to " + name + " \u{1F44B}\n\nHere's what I can help you with:\n\n\u{1F4DD} Create a Job Description \u2014 Describe your ideal candidate\n\u{1F4E2} Post a Job \u2014 Publish to LinkedIn or Naukri\n\u{1F50D} Source Candidates \u2014 Find and manage talent\n\nJust tell me what you'd like to do!",
      timestamp: new Date().toISOString(),
      metadata: { kind: 'default_welcome' },
    },
  ];
};

const ALL_POST_CHANNELS = ['LinkedIn', 'Naukri'];

const normalizeProfileList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];

    try {
      const parsed = JSON.parse(trimmed);
      return normalizeProfileList(parsed);
    } catch {
      return trimmed
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
    }
  }

  if (typeof value === 'object') {
    return Object.values(value).filter(Boolean);
  }

  return [value].filter(Boolean);
};

const getProfileListLabel = (item) => {
  if (typeof item === 'string') return item;
  if (!item || typeof item !== 'object') return String(item || '');
  return item.name || item.skill || item.label || item.title || item.text || '';
};

const normalizeCandidateStatus = (candidate) => (candidate?.funnel_stage || candidate?.stage || candidate?.status || candidate?.candidate_status || 'Sourced')
  .toString()
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '');

const getResumeLink = (candidate) => candidate?.resume_url || candidate?.resume_link || candidate?.cv_url || candidate?.file_url || candidate?.resume_path || candidate?.file_path || '';

const getInterviewTimestamp = (interview) => {
  const scheduledDate = interview?.scheduled_date;
  const scheduledTime = interview?.scheduled_time || '00:00:00';
  if (!scheduledDate) return 0;
  const parsed = new Date(`${scheduledDate}T${scheduledTime}`);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};

const completedInterviewStatuses = ['completed', 'recording_completed', 'meeting_ended', 'ended', 'left', 'done', 'finished', 'disconnected', 'closed'];

const isInterviewCompleted = (interview) => {
  if (!interview) return false;
  const interviewStatus = String(interview.status || '').trim().toLowerCase();
  const aiNoteStatus = String(interview.ai_note_taker_status || '').trim().toLowerCase();
  return Boolean(
    interview.meeting_completed ||
    completedInterviewStatuses.includes(interviewStatus) ||
    completedInterviewStatuses.includes(aiNoteStatus)
  );
};

const hasInterviewEnded = (interview) => {
  const startAt = getInterviewTimestamp(interview);
  if (!startAt) return false;
  const durationMinutes = Number(interview?.duration) || 60;
  return (startAt + (durationMinutes * 60 * 1000)) <= Date.now();
};

const getCandidateInterviewKey = (candidate) => {
  const emailKey = String(candidate?.email || '').trim().toLowerCase();
  if (!emailKey) return '';
  const searchKey = String(candidate?.search_id || candidate?.searchId || '').trim();
  return searchKey ? `${emailKey}::${searchKey}` : emailKey;
};

const getCandidateEmailKey = (candidate) => String(candidate?.email || '').trim().toLowerCase();

const normalizeBoolean = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.trim().toLowerCase() === 'true';
  return Boolean(value);
};

const hasCandidateResume = (candidate) => Boolean(
  candidate?.doc_id ||
  candidate?.cv_filename ||
  candidate?.resume_filename ||
  candidate?.attachment_location ||
  getResumeLink(candidate) ||
  normalizeBoolean(candidate?.resume_uploaded) ||
  normalizeBoolean(candidate?.has_resume)
);

const isImportedCandidate = (candidate) => {
  const source = String(candidate?.source_type || candidate?._source_type || candidate?.source || '').toLowerCase();
  return source.includes('import');
};

const getScheduledInterviewInfo = (candidate, interviewsByCandidateKey = {}) => {
  if (!candidate) return null;

  const candidateKey = getCandidateInterviewKey(candidate);
  const directInterview = {
    id: candidate.interview_id || candidate.id || null,
    search_id: candidate.search_id || candidate.searchId || null,
    candidate_email: candidate.email || '',
    scheduled_date: candidate.scheduled_date || null,
    scheduled_time: candidate.scheduled_time || null,
    google_meet_link: candidate.google_meet_link || null,
    booking_status: candidate.booking_status || candidate.interview_booking_status || null,
    status: candidate.interview_status || candidate.status || null,
    ai_note_taker_status: candidate.ai_note_taker_status || null,
    meeting_completed: candidate.meeting_completed || false,
    duration: candidate.duration || null,
    interviewer_name: candidate.interviewer_name || null,
  };
  const hasDirectInterviewDetails = Boolean(
    directInterview.scheduled_date ||
    directInterview.scheduled_time ||
    directInterview.google_meet_link ||
    directInterview.booking_status
  );

  const interview = hasDirectInterviewDetails
    ? directInterview
    : (candidateKey ? interviewsByCandidateKey[candidateKey] || null : null);

  if (!interview) return null;

  const status = String(interview.status || '').trim().toLowerCase();
  const bookingStatus = String(interview.booking_status || '').trim().toLowerCase();
  if (status === 'cancelled') return null;
  // "Completed" must mean the candidate ACTUALLY attended and the interview concluded — never just that
  // the scheduled slot time has elapsed. A no-show whose slot passed used to be marked completed here
  // (via hasInterviewEnded), which wrongly surfaced an empty "Interview Report" for a candidate who never
  // attended. Gate purely on real conclusion signals (meeting_completed / status / note-taker status).
  if (isInterviewCompleted(interview)) {
    return {
      ...interview,
      lifecycle: 'completed',
    };
  }
  if (!interview.google_meet_link && !interview.scheduled_date && !interview.scheduled_time && bookingStatus !== 'booked') {
    return null;
  }

  return {
    ...interview,
    lifecycle: 'scheduled',
  };
};

const getCandidateInterviewLockInfo = (candidate, interviewsByCandidateKey = {}, fallbackToEmail = true) => {
  if (!candidate) return null;
  const candidateKey = getCandidateInterviewKey(candidate);
  const emailKey = getCandidateEmailKey(candidate);
  const mappedInterview = (
    (candidateKey && interviewsByCandidateKey[candidateKey]) ||
    (fallbackToEmail && emailKey && interviewsByCandidateKey[emailKey]) ||
    null
  );
  const hasCandidateInterviewRecord = Boolean(
    candidate.interview_id ||
    candidate.interviewId ||
    candidate.booking_status ||
    candidate.interview_booking_status ||
    candidate.interview_status ||
    candidate.scheduled_date ||
    candidate.scheduled_time ||
    candidate.google_meet_link
  );
  const interview = mappedInterview || (hasCandidateInterviewRecord ? candidate : null);
  if (!interview) return null;

  const status = String(interview.status || interview.interview_status || '').trim().toLowerCase();
  const bookingStatus = String(interview.booking_status || interview.interview_booking_status || '').trim().toLowerCase();
  const aiStatus = String(interview.ai_note_taker_status || '').trim().toLowerCase();
  const hasStarted = Boolean(
    interview.id ||
    interview.interview_id ||
    interview.scheduled_date ||
    interview.scheduled_time ||
    interview.google_meet_link ||
    bookingStatus ||
    status ||
    aiStatus
  );
  return hasStarted ? { ...interview, status, booking_status: bookingStatus } : null;
};

const canGenerateManualInterviewQuestions = (candidate, interviewLockInfo) => {
  const interview = interviewLockInfo || {};
  const type = String(
    interview.interview_type ||
    interview.type ||
    candidate?.interview_type ||
    candidate?.interviewType ||
    ''
  ).trim().toLowerCase();
  const isManual = type === 'manual' || type === 'human' || type === 'in_person' || type === 'video';
  const alreadyGenerated = Boolean(
    candidate?.ai_questions_generated ||
    candidate?.generated_interview_questions ||
    candidate?.interview_questions_generated ||
    interview.ai_questions_generated ||
    interview.generated_interview_questions ||
    interview.interview_questions_generated
  );
  return Boolean(interviewLockInfo && isManual && !alreadyGenerated);
};

const formatInterviewDateLabel = (scheduledDate) => {
  if (!scheduledDate) return '';
  const parsed = new Date(`${scheduledDate}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return scheduledDate;
  return parsed.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const formatInterviewTimeLabel = (scheduledTime) => {
  if (!scheduledTime) return '';
  const parsed = new Date(`1970-01-01T${scheduledTime}`);
  if (Number.isNaN(parsed.getTime())) return scheduledTime;
  return parsed.toLocaleTimeString('en-IN', {
    hour: 'numeric',
    minute: '2-digit',
  });
};

const getCandidateActionPlan = (candidate, interviewsByCandidateKey = {}, context = {}) => {
  const status = normalizeCandidateStatus(candidate);
  const hasContactInfo = candidate?.email || candidate?.phone;
  const interviewInfo = getScheduledInterviewInfo(candidate, interviewsByCandidateKey);
  const interviewLockInfo = context.interviewLockInfo || getCandidateInterviewLockInfo(candidate, interviewsByCandidateKey);
  const hasScheduledInterview = Boolean(interviewInfo && interviewInfo.lifecycle === 'scheduled');
  const hasCompletedInterview = Boolean(interviewInfo && interviewInfo.lifecycle === 'completed');
  const scheduleMeetingAction = { key: 'schedule_meeting', label: 'Schedule Meeting' };
  const hasOutreach = Boolean(context.hasEmailOutreach || candidate?.outreach_sent || candidate?.outreach_count);
  const hasResume = context.hasResume ?? hasCandidateResume(candidate);
  const isInterested = status.includes('interest');

  if (hasCompletedInterview) {
    return [];
  }
  if (interviewLockInfo) {
    return status.includes('contact') || status.includes('outreach') || status.includes('interest') || status.includes('shortlist')
      ? [{ key: 'follow_up', label: 'Do Follow-up' }]
      : [];
  }

  if (isImportedCandidate(candidate) && !hasOutreach) {
    const actions = [{ key: 'send_outreach', label: 'Send Outreach' }];
    if (hasContactInfo && !hasScheduledInterview) actions.push(scheduleMeetingAction);
    return actions;
  }

  if (isInterested && !hasResume) {
    const actions = [{ key: 'request_resume', label: 'Request Resume' }];
    if (hasOutreach) actions.push({ key: 'follow_up', label: 'Do Follow-up' });
    return actions;
  }

  if (status.includes('interview')) {
    const actions = [{ key: 'follow_up', label: 'Do Follow-up' }];
    if (hasContactInfo && !hasScheduledInterview) actions.push(scheduleMeetingAction);
    return actions;
  }
  if (status.includes('contact') || status.includes('outreach')) {
    return [{ key: 'follow_up', label: 'Do Follow-up' }];
  }
  if (status.includes('interest') || status.includes('shortlist') || status.includes('schedule')) {
    const actions = [];
    if (hasContactInfo && !hasScheduledInterview) actions.push(scheduleMeetingAction);
    actions.push({ key: 'follow_up', label: 'Do Follow-up' });
    return actions;
  }
  return [{ key: 'send_outreach', label: 'Send Outreach' }];
};

const getCandidateActionCommand = (actionKey, candidate) => {
  const candidateName = candidate?.name || 'this candidate';
  const candidateEmail = candidate?.email ? ` (${candidate.email})` : '';
  const candidateContext = `${candidateName}${candidateEmail}`;

  switch (actionKey) {
    case 'schedule_meeting':
      return `Schedule interview with ${candidateContext}.`;
    case 'follow_up':
      return `Send follow-up email to ${candidateName}${candidate?.email ? ` (${candidate.email})` : ''}. This is a follow-up to previous outreach for this specific candidate only — do not contact other candidates.`;
    case 'request_resume':
      return `Request resume from ${candidateContext}. This is a resume request for this specific candidate only — do not send initial outreach or follow-up.`;
    default:
      return `Send outreach email to ${candidateName}${candidate?.email ? ` (${candidate.email})` : ''}.`;
  }
};

const CandidateSourceIcon = ({ source }) => {
  const label = (source || '').toString().toLowerCase();
  const base = 'w-4 h-4 rounded flex items-center justify-center text-[8px] font-bold flex-shrink-0';
  if (label.includes('linkedin') || label === 'in') return <span className={base} style={{ background: '#0A66C2', color: '#fff' }}>in</span>;
  if (label.includes('naukri')) return <span className={base} style={{ background: '#0B5ED7', color: '#fff' }}>N</span>;
  if (label.includes('resume')) return <span className={base} style={{ background: '#10B981', color: '#fff' }}>R</span>;
  if (label.includes('google') || label.includes('online') || label.includes('web')) {
    return (
      <div style={{ width: 16, height: 16, borderRadius: '50%', background: '#fff', border: '1px solid #D1D5DB', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <span style={{ fontSize: 8, fontWeight: 700, background: 'conic-gradient(from 180deg at 50% 50%, #4285F4 0deg, #4285F4 90deg, #34A853 90deg, #34A853 180deg, #FBBC05 180deg, #FBBC05 270deg, #EA4335 270deg)', WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>G</span>
      </div>
    );
  }
  return <span className={base} style={{ background: '#8B5CF6', color: '#fff' }}>J</span>;
};

const avatarColors = [
  { bg: '#E6F1FB', color: '#185FA5' },
  { bg: '#E1F5EE', color: '#0F6E56' },
  { bg: '#FAEEDA', color: '#854F0B' },
  { bg: '#EEEDFE', color: '#534AB7' },
  { bg: '#FAECE7', color: '#993C1D' }
];

function CandidateAvatar({ name, index = 0, size = 24 }) {
  const initials = (name || 'C').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
  const av = avatarColors[index % avatarColors.length];
  return (
    <div style={{
      width: size,
      height: size,
      borderRadius: '50%',
      background: av.bg,
      color: av.color,
      fontSize: size * 0.4,
      fontWeight: 600,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0
    }}>
      {initials}
    </div>
  );
}

const ChatMain = ({
  jobTitle,
  lastUpdated,
  duration,
  showHeader = true,
  onUserQuery,
  selectedJobId,
  syncSearchId = null, // searchId for persistent sync progress banner via SyncContext
  onCandidateRejected,
  onSystemMessage,
  onSystemMessages = [],
  systemMessageToken = 0, // Token to force re-add message even if same content
  lastResponseMode = null, // "agent" | "ask" — mode of last lifecycle response
  chatMode = 'agent', // Current chat mode toggle: "agent" | "ask"
  onChatModeChange = null, // Callback to change chat mode
  isLoading = false,
  sourcingComplete = false, // true when sourcing process is complete
  jobPostingPhase = 'idle', // idle | channel_selection | collecting_details | posting | posted
  selectedChannels = [],
  channelOptions = ['LinkedIn', 'Naukri'],
  postedChannels = [],
  onChannelSelect = null,
  jobPostingProcessing = false, // true when job posting is in progress
  jobPostingComplete = false, // true when job posting is done (show indicator with 0.2 opacity)
  jobPostingFailed = false, // true if job posting failed
  waitingForBackendQuestion = false, // true when waiting for backend to ask questions (company name, salary, etc.)
  waitingForConfirmation = false, // true when waiting for user to confirm requirements
  pendingNaukriFieldOptions = [], // dropdown options for Naukri field selection
  onNaukriFieldOptionSelect = null, // callback when user selects an option
  initialMessages = [], // full history loaded from backend when viewing previous job
  resetToken = 0, // increments when user clicks "New Job Posting"
  historyLoading = false, // true while fetching chat history for previous job
  contextActions = [], // contextual quick actions derived from session state
  onOpenScorecardEditor = null,
  scorecardEditorAvailable = false,
  scorecardSavedSummary = '',
  autoSendMessage = '',
  autoSendToken = 0,
  autoSendOptions = null,
  onAutoSendConsumed = null,
  draftEditActions = [], // Edit/Send/Cancel buttons for draft previews
  workspaceOptions = [], // Workspaces shown during JD creation
  workspaceSelectionRequired = false, // JD confirmation waits for a workspace
  onDraftEditAction = null, // callback when draft edit action is clicked
  stageLabel = '', // LangGraph agent stage label (e.g. "JD Creation")
  stageCancellable = false, // whether the stage can be cancelled
  onStageCancel = null, // callback when user cancels the active agent stage
  transitionPill = '', // Single transition pill text to inject (e.g. "__AGENT_TRANSITION__Filtering Agent activated")
  transitionPillToken = 0, // Increments to force-inject even if same text
  filterBreadcrumbs = [], // Ask-mode filter trail: ["3+ yrs", "React", "Chennai"]
  filterActions = [], // Ask-mode action buttons with handoff info
  followUpSuggestions = [], // Ask-mode follow-up chip suggestions [{label, kind}]
  newCandidatesCount = 0, // Ask-mode hint: candidates added since last filter run that match the chain
  onRefreshFilter, // Click handler for the "include new" chip — re-runs the active filter chain
  onFilterAction = null, // callback when filter action button clicked (handoff to Agent)
  onClearFilters = null, // callback to clear all filters
  onStartOutreach = null, // callback to start outreach for a candidate
  onViewCandidateDetails = null, // callback to open the full candidate profile
  onCandidateAction = null, // callback for candidate-specific actions from inline card
  onAttachmentClick = null, // callback to open resume upload from embedded chat
  hasJd = false,            // true when the current session already has a JD
  jdChecking = false,       // true while session data is still loading (button disabled)
  showFilterMode = true,
  canUseOutreach = true,
  onMarkPriority = null, // callback to mark candidate as priority
  onSendFollowup = null, // callback to send follow-up to candidate
  onBookInterview = null, // callback to book interview for candidate
  onViewResume = null, // callback to view resume in popup modal
  onGenerateInterview = null, // callback to generate AI interview questions
  onMeetingAnalysis = null, // callback to view meeting analysis
  candidates = [], // full candidates array for looking up latest data
  renderInterviewCreation = null, // renders inline interview creation card messages
}) => {
  const { settings } = useSettings();
  const { isDark } = useTheme();
  const { getSyncState, currentSearchId, getActiveSyncs } = useSyncContext();
  const activeSyncs = getActiveSyncs();
  const effectiveSyncSearchId =
    syncSearchId ||
    currentSearchId ||
    (activeSyncs.length > 0 ? activeSyncs[activeSyncs.length - 1].searchId : null);
  const syncState = getSyncState(effectiveSyncSearchId);
  const isSyncing = syncState.syncing;
  const syncLogs = Array.isArray(syncState.logs) ? syncState.logs : [];
  const syncPhase = syncState.phase;
  const { formatDateSeparator: tzFormatDateSeparator, getDateString: tzGetDateString } = useTimezone();
  const primaryColor = settings.theme?.colors?.primary || '#3B5BDB';
  const secondaryColor = settings.theme?.colors?.secondary || '#20C997';
  // Start with empty; we'll either hydrate from initialMessages (previous job)
  // or inject the default welcome message for a brand new session.
  const [messages, setMessages] = useState(() => []);
  const [draftTemplateEditor, setDraftTemplateEditor] = useState({
    open: false,
    messageId: null,
    value: '',
    error: '',
  });
  const [workspaceFilter, setWorkspaceFilter] = useState('');
  const [selectedWorkspaceName, setSelectedWorkspaceName] = useState('');
  const [workspaceDropdownOpen, setWorkspaceDropdownOpen] = useState(false);
  const [creatingWorkspace, setCreatingWorkspace] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState('');

  useEffect(() => {
    if (!workspaceSelectionRequired) {
      setWorkspaceFilter('');
      setSelectedWorkspaceName('');
      setWorkspaceDropdownOpen(false);
      setCreatingWorkspace(false);
      setNewWorkspaceName('');
      return;
    }
    if (!Array.isArray(workspaceOptions) || workspaceOptions.length === 0) {
      setSelectedWorkspaceName('');
      setCreatingWorkspace(true);
      return;
    }
    setCreatingWorkspace(false);
    setSelectedWorkspaceName((current) => (
      workspaceOptions.some((workspace) => workspace.name === current)
        ? current
        : workspaceOptions[0]?.name || ''
    ));
  }, [workspaceOptions, workspaceSelectionRequired]);
  const filteredWorkspaceOptions = useMemo(() => (
    Array.isArray(workspaceOptions)
      ? workspaceOptions.filter((workspace) => (
        workspace.name.toLowerCase().includes(workspaceFilter.trim().toLowerCase())
      ))
      : []
  ), [workspaceFilter, workspaceOptions]);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const processedSystemMsgCountRef = useRef(0); // Track how many systemMessages have been processed
  const lastPillTokenRef = useRef(0); // Track last processed pill token
  const [userScrolledUp, setUserScrolledUp] = useState(false);
  const scrollDebounceRef = useRef(null);
  const [currentJobId, setCurrentJobId] = useState(null);
  const [inputMessage, setInputMessage] = useState('');
  const [candidateCardAnchorId, setCandidateCardAnchorId] = useState(null);
  const [expandedCandidateSummaries, setExpandedCandidateSummaries] = useState({});
  const inputRef = useRef(null);
  const lastAutoSendTokenRef = useRef(0);
  const lastSystemMessageTokenRef = useRef(0);
  const [candidateCardStates, setCandidateCardStates] = useState({}); // Track accordion states for candidate cards
  const [candidateNotes, setCandidateNotes] = useState({}); // Track notes for candidates
  const [noteInputVisible, setNoteInputVisible] = useState({}); // Track which candidate note input is visible
  const [outreachLoading, setOutreachLoading] = useState({}); // Track which candidates are currently being sent outreach
  const [cardEmailData, setCardEmailData] = useState({}); // { [cardKey]: { loading, replies } } — lazy-loaded per card
  const pendingFollowupRef = useRef(null); // { msgId, candidate } — set while warning bubble is active
  const [interviewsByCandidateKey, setInterviewsByCandidateKey] = useState({});
  const [interviewCreationInputs, setInterviewCreationInputs] = useState({});
  const [meetingAnalysisModal, setMeetingAnalysisModal] = useState({
    isOpen: false,
    loading: false,
    candidate: null,
    analysis: null,
    aiResult: null,
    error: null,
  });
  const [rejectCandidate, setRejectCandidate] = useState(null);
  // Keys (id:/email:) of candidates rejected in this session — their chat cards are hidden
  // immediately since the cards render from static message data, not the candidates array.
  const [rejectedKeys, setRejectedKeys] = useState(() => new Set());
  // JD upload modal state
  const [jdUploadModal, setJdUploadModal] = useState({
    isOpen: false,
    step: 'pick',       // 'pick' | 'uploading' | 'review' | 'filling'
    file: null,
    jdText: '',
    missingFields: [],  // [{key, label, question}]
    fieldValues: {},    // {key: value}
    fieldIndex: 0,      // which missing field is currently being filled
    error: '',
  });
  const jdFileInputRef = useRef(null);
  const lastCandidateCardIdRef = useRef(null); // Track last candidate card ID to detect new additions

  const extractTemplateFromOutreachPreview = (rawMessage = '') => {
    let text = String(rawMessage || '').trim();
    text = text.replace(/^Switching to\s+\*\*?Outreach\*\*?\s+—\s+let'?s reach out to candidates\.\s*/im, '');
    text = text.replace(/^Switching to Outreach\s+—\s+let'?s reach out to candidates\.\s*/im, '');
    text = text.replace(/^📧\s*\*\*Outreach Template (Generated|Updated)\*\*.*?\n\n/s, '');
    text = text.replace(/^📧\s*\*\*Outreach Template (Generated|Updated)\*\*.*?$/m, '');
    text = text.replace(/^[^\n]*Email Message \(for [^\n]*\n*/im, '');
    text = text.replace(/^Switching to\s+\*\*?Outreach\*\*?\s+—\s+let'?s reach out to candidates\.\s*/im, '');
    text = text.replace(/^Switching to Outreach\s+—\s+let'?s reach out to candidates\.\s*/im, '');

    const linkedinIndex = text.search(/\n\s*(💼\s*)?\*\*💼?\s*LinkedIn Message/i);
    if (linkedinIndex >= 0) text = text.slice(0, linkedinIndex);

    const recipientsIndex = text.search(/\n\s*\*\*Recipients\s*\(/i);
    if (recipientsIndex >= 0) text = text.slice(0, recipientsIndex);

    text = text
      .replace(/\n-{3,}\n/g, '\n')
      .replace(/^\*\*Signature:\*\*\s*/im, '')
      .replace(/\*\*/g, '')
      .trim();

    return text;
  };

  const getLatestOutreachPreviewMessage = () => {
    return [...messages]
      .reverse()
      .find((msg) =>
        msg?.sender === 'agent' &&
        typeof msg?.message === 'string' &&
        /Outreach Template (Generated|Updated)|Recipients \(/i.test(msg.message)
      );
  };

  const getLatestOutreachPreviewText = () => {
    const latestPreview = getLatestOutreachPreviewMessage();
    return extractTemplateFromOutreachPreview(latestPreview?.message || '');
  };

  const openInlineTemplateEditor = () => {
    const latestPreview = getLatestOutreachPreviewMessage();
    const currentTemplate = getLatestOutreachPreviewText();
    setDraftTemplateEditor({
      open: true,
      messageId: latestPreview?.id || null,
      value: currentTemplate,
      error: currentTemplate ? '' : 'Could not read the current template. Paste the template text here.',
    });
  };

  const lastOutreachClearedMsgIdRef = useRef(null); // Track last msg ID that triggered cardEmailData cache clear
  const postedSet = new Set(
    (Array.isArray(postedChannels) ? postedChannels : []).map((c) => String(c || '').toLowerCase())
  );
  const normalizedProvidedChannels = Array.isArray(channelOptions)
    ? channelOptions.filter((channel) => typeof channel === 'string' && channel.trim() !== '')
    : [];
  const effectiveChannelOptions =
    normalizedProvidedChannels.length > 0
      ? Array.from(
          new Set([
            ...normalizedProvidedChannels,
            ...ALL_POST_CHANNELS.filter((channel) => !postedSet.has(channel.toLowerCase())),
          ])
        ).filter((channel) => !postedSet.has(channel.toLowerCase()))
      : ALL_POST_CHANNELS.filter((channel) => !postedSet.has(channel.toLowerCase()));

  const scrollToBottom = () => {
    if (userScrolledUp) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Check if a NEW candidate card was just added - if so, force scroll once
  const shouldForceScroll = () => {
    const lastMsg = messages[messages.length - 1];
    if (lastMsg?.metadata?.type === 'candidate_card' || lastMsg?.metadata?.type === 'interview_creation') {
      const currentCardId = lastMsg.id;
      // Only force scroll if this is a new candidate card (different from last tracked)
      if (currentCardId !== lastCandidateCardIdRef.current) {
        lastCandidateCardIdRef.current = currentCardId;
        return true;
      }
    }
    return false;
  };

  // Force scroll to bottom (used by the floating arrow button)
  const forceScrollToBottom = () => {
    if (scrollDebounceRef.current) clearTimeout(scrollDebounceRef.current);
    setUserScrolledUp(false);
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Scroll to bottom without disabling the scroll arrow (for candidate cards)
  const scrollToBottomWithoutDisablingArrow = () => {
    if (scrollDebounceRef.current) clearTimeout(scrollDebounceRef.current);
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const hasActiveSyncState =
    Boolean(effectiveSyncSearchId) &&
    (isSyncing || syncPhase === 'complete' || syncPhase === 'error' || syncLogs.length > 0);
  const candidateInterviewKeys = useMemo(
    () => Array.from(
      new Set(
        [
          ...candidates.map((candidate) => getCandidateInterviewKey({
            ...candidate,
            search_id: candidate?.search_id || candidate?.searchId || candidate?._searchId || effectiveSyncSearchId || selectedJobId || '',
          })),
          ...messages.map((message) => {
            const candidate = message?.metadata?.candidate;
            return getCandidateInterviewKey({
              ...candidate,
              search_id: candidate?.search_id || candidate?.searchId || candidate?._searchId || effectiveSyncSearchId || selectedJobId || '',
            });
          }),
        ]
          .map((candidateKey) => String(candidateKey || '').trim())
          .filter(Boolean)
      )
    ),
    [candidates, effectiveSyncSearchId, messages, selectedJobId]
  );
  const candidateInterviewEmailKeys = useMemo(
    () => Array.from(
      new Set(
        [
          ...candidates.map((candidate) => getCandidateEmailKey(candidate)),
          ...messages.map((message) => getCandidateEmailKey(message?.metadata?.candidate)),
        ].filter(Boolean)
      )
    ),
    [candidates, messages]
  );

  // Format date for date separator — uses company timezone from TimezoneContext
  const formatDateSeparator = (dateString) => tzFormatDateSeparator(dateString);

  // Get date string for a message (for comparison) — uses company timezone
  const getMessageDateString = (timestamp) => tzGetDateString(timestamp);

  useEffect(() => {
    if (shouldForceScroll()) {
      scrollToBottomWithoutDisablingArrow();
    } else {
      scrollToBottom();
    }
  }, [messages, isLoading, userScrolledUp]);

  // When parent sends a new initialMessages, merge it into local state
  // by id rather than replacing wholesale.
  //
  // Why merge (not replace):
  //   * In Filter mode, user-typed messages and agent responses get
  //     added to ChatMain's local `messages` via handleSendMessage and
  //     the systemMessages flow — they are NOT pushed back to the
  //     parent's chatHistory.
  //   * If parent's chatHistory then changes (e.g. on mode toggle when
  //     we add an `agent_transition` entry), this useEffect fires
  //     again. A wholesale replace would drop every filter-mode message
  //     because they aren't in chatHistory. The user sees their entire
  //     filter conversation vanish on toggle.
  //
  // Why dedup by id only (not by content):
  //   * Transition pills like "Pipeline Agent activated" can legitimately
  //     repeat across a session (toggle in → toggle out → toggle in
  //     again). A content-based dedup would silently drop the second
  //     and later occurrences and the chat would look broken.
  //   * All locally-added messages get unique ids via makeMsgId(); all
  //     server-loaded messages have DB-assigned ids. id-only dedup is
  //     correct under both producers.
  //
  // Wholesale replace on selectedJobId change is still correct (different
  // job = different conversation); that case is handled separately
  // below in the `selectedJobId !== currentJobId` effect.
  useEffect(() => {
    if (historyLoading) return;
    if (!Array.isArray(initialMessages) || initialMessages.length === 0) return;
    setMessages((prev) => {
      const normalizedInitialMessages = initialMessages.map((msg) => ({
        ...msg,
        timestamp: msg?.timestamp || new Date().toISOString(),
      }));

      if (prev.length === 0) {
        return normalizedInitialMessages;
      }

      const prevIds = new Set();
      for (const m of prev) {
        if (m?.id !== undefined && m?.id !== null) prevIds.add(m.id);
      }

      const merged = [...prev];
      for (const msg of normalizedInitialMessages) {
        const id = msg?.id;
        if (id !== undefined && id !== null && prevIds.has(id)) continue;
        merged.push(msg);
      }
      return merged.length === prev.length ? prev : merged;
    });
  }, [historyLoading, initialMessages, selectedJobId]);

  // When resetToken changes (e.g., New Job Posting clicked),
  // clear local messages and job tracking so we start fresh.
  useEffect(() => {
    if (resetToken > 0) {
      setMessages([]);
      setCurrentJobId(null);
    }
  }, [resetToken]);

  // Show welcome message for brand new sessions (no chat history to restore).
  // Triggers when: no initialMessages AND messages empty (after reset or first load).
  useEffect(() => {
    if (historyLoading) return;
    if (stageLabel) return;
    if ((onSystemMessage || '').trim()) return;
    if (Array.isArray(onSystemMessages) && onSystemMessages.length > 0) return;
    if (
      (!initialMessages || initialMessages.length === 0) &&
      messages.length === 0
    ) {
      setMessages(createInitialMessages(settings?.app?.name));
    }
  }, [
    selectedJobId,
    historyLoading,
    initialMessages,
    messages.length,
    onSystemMessage,
    onSystemMessages,
    settings?.app?.name,
    stageLabel,
  ]);

  useEffect(() => {
    const hasHydratedConversation =
      (Array.isArray(initialMessages) && initialMessages.length > 0) ||
      Boolean((onSystemMessage || '').trim()) ||
      (Array.isArray(onSystemMessages) && onSystemMessages.length > 0) ||
      Boolean(stageLabel);

    if (!hasHydratedConversation) return;

    setMessages((prev) => {
      if (prev.length !== 1) return prev;
      if (prev[0]?.metadata?.kind !== 'default_welcome') return prev;
      return [];
    });
  }, [initialMessages, onSystemMessage, onSystemMessages, stageLabel]);

  useEffect(() => {
    if (candidateInterviewKeys.length === 0) {
      setInterviewsByCandidateKey({});
      return undefined;
    }

    let cancelled = false;
    const loadCandidateInterviews = async () => {
      try {
        const query = effectiveSyncSearchId
          ? `?search_id=${encodeURIComponent(String(effectiveSyncSearchId))}`
          : '';
        const data = await aiAutomationApi.get(`/interviews/calendar/all${query}`);
        if (cancelled) return;
        const interviews = Array.isArray(data?.interviews) ? data.interviews : [];
        const nextByCandidateKey = {};

        interviews
          .filter((interview) => candidateInterviewEmailKeys.includes(getCandidateEmailKey({ email: interview?.candidate_email })))
          .sort((a, b) => getInterviewTimestamp(b) - getInterviewTimestamp(a))
          .forEach((interview) => {
            const candidateKey = getCandidateInterviewKey({
              email: interview?.candidate_email,
              search_id: interview?.search_id,
            });
            if (!candidateKey || nextByCandidateKey[candidateKey]) return;
            const normalized = getScheduledInterviewInfo(interview, {});
            const interviewRecord = normalized || interview;
            if (interviewRecord) {
              nextByCandidateKey[candidateKey] = interviewRecord;
              const emailKey = getCandidateEmailKey({ email: interview?.candidate_email });
              if (!effectiveSyncSearchId && emailKey && !nextByCandidateKey[emailKey]) {
                nextByCandidateKey[emailKey] = interviewRecord;
              }
            }
          });

        setInterviewsByCandidateKey(nextByCandidateKey);
      } catch {
        if (!cancelled) setInterviewsByCandidateKey({});
      }
    };

    loadCandidateInterviews();
    return () => { cancelled = true; };
  }, [candidateInterviewEmailKeys, candidateInterviewKeys, effectiveSyncSearchId]);

  // Eagerly fetch email data for candidate cards as they appear in messages
  useEffect(() => {
    const cardMessages = messages.filter(m => m?.metadata?.type === 'candidate_card' && m?.metadata?.candidate?.email);
    cardMessages.forEach(m => {
      const cvc = m.metadata.candidate;
      const cardKey = `candidate-card-${m.id}`;
      if (cardEmailData[cardKey]) return; // already fetched
      const cEmail = cvc.email;
      const resolvedSearchId = cvc._searchId || cvc.search_id || cvc.searchId || effectiveSyncSearchId || selectedJobId || '';
      const resolvedCandidateId = cvc.id || cvc.candidate_id || '';
      setCardEmailData(prev => {
        if (prev[cardKey]) return prev; // already triggered
        return { ...prev, [cardKey]: { loading: true, replies: [] } };
      });
      const params = new URLSearchParams();
      if (resolvedSearchId) params.set('search_id', resolvedSearchId);
      if (resolvedCandidateId) params.set('candidate_id', String(resolvedCandidateId));
      aiAutomationApi.get(`/email/replies/candidate/${encodeURIComponent(cEmail)}${params.toString() ? `?${params.toString()}` : ''}`)
        .then(data => setCardEmailData(prev => ({ ...prev, [cardKey]: { loading: false, replies: data?.replies || [] } })))
        .catch(() => setCardEmailData(prev => ({ ...prev, [cardKey]: { loading: false, replies: [] } })));
    });
  }, [messages, selectedJobId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh candidate card email data when outreach is sent
  // Detects the last agent message for outreach-sent signals and clears the cache
  // so the next card open re-fetches fresh data (button changes from "Send Outreach" → "Send Follow-up")
  useEffect(() => {
    const agentMessages = messages.filter(m => m?.sender === 'agent' && typeof m.message === 'string');
    if (!agentMessages.length) return;
    const lastAgent = agentMessages[agentMessages.length - 1];
    if (!lastAgent || lastOutreachClearedMsgIdRef.current === lastAgent.id) return;
    const content = (lastAgent.message || '').toLowerCase();
    const isOutreachSent =
      (lastAgent.metadata?.type === 'lifecycle_outreach_sent') ||
      content.includes('outreach sent') ||
      content.includes('sent outreach') ||
      content.includes('outreach completed') ||
      content.includes('email has been sent') ||
      content.includes('emails have been sent') ||
      content.includes('outreach email sent') ||
      (content.includes('sent') && content.includes('recipients'));
    if (isOutreachSent) {
      lastOutreachClearedMsgIdRef.current = lastAgent.id;
      // Clear all cached card email data — the eager-fetch useEffect will re-fetch on next render
      setCardEmailData({});
    }
  }, [messages]); // eslint-disable-line react-hooks/exhaustive-deps

  // Only reset messages when user explicitly selects a DIFFERENT job from sidebar.
  // Don't reset the first time a job id is assigned for a brand new conversation.
  useEffect(() => {
    if (!selectedJobId) return;

    if (currentJobId === null) {
      // First time a job id is set (new conversation or after reset) – track it
      setCurrentJobId(selectedJobId);
      return;
    }

    // Different job selected — wipe local state; parent will rehydrate from history.
    if (selectedJobId !== currentJobId) {
      setMessages([]);
      setCurrentJobId(selectedJobId);
    }
  }, [selectedJobId, currentJobId]);

  // Add system messages from backend when provided (multiple messages)
  // Process ONLY new systemMessages (avoids re-adding old messages on every change).
  // Uses processedSystemMsgCountRef to track what's already been rendered.
  // systemMessageToken forces re-processing from index 0 (for responses that need full re-add).
  useEffect(() => {
    if (onSystemMessages && Array.isArray(onSystemMessages) && onSystemMessages.length > 0) {
      const tokenChanged = systemMessageToken !== lastSystemMessageTokenRef.current;
      lastSystemMessageTokenRef.current = systemMessageToken;

      // Determine which messages to process
      const startIdx = tokenChanged ? 0 : processedSystemMsgCountRef.current;
      const newMsgs = onSystemMessages.slice(startIdx);

      if (newMsgs.length > 0) {
        setMessages((prev) => {
          const toAdd = [];
          let nextId = prev.length + 1;

          newMsgs.forEach((msg) => {
            if (msg && msg.trim()) {
              const isChannelSelectionMsg = msg.includes('Select the Channel you want to post');
              const msgTrimmed = msg.trim();
              const shouldForceDedup =
                msgTrimmed.startsWith('__AGENT_TRANSITION__') ||
                msgTrimmed.toLowerCase().startsWith('candidate sourcing completed') ||
                msgTrimmed.includes('Should candidates be allowed to call?') ||
                msgTrimmed.startsWith('✅ Got it -');
              const recentAgentMessages = prev
                .filter((m) => m.sender === 'agent' && typeof m.message === 'string')
                .slice(-8);

              const isDuplicate = !isChannelSelectionMsg &&
                ((!tokenChanged || shouldForceDedup) &&
                  recentAgentMessages.some((m) => m.message.trim() === msgTrimmed));

              if (!isDuplicate) {
                toAdd.push({
                  id: nextId++,
                  sender: 'agent',
                  message: msg,
                  timestamp: new Date().toISOString(),
                  mode: lastResponseMode || undefined,
                });
              }
            }
          });

          return toAdd.length > 0 ? [...prev, ...toAdd] : prev;
        });

        // Clear outreach loading state when outreach-related messages are received
        const hasOutreachMessage = newMsgs.some(msg => 
          msg && (
            msg.toLowerCase().includes('outreach') ||
            msg.toLowerCase().includes('email sent') ||
            msg.toLowerCase().includes('template generated') ||
            msg.toLowerCase().includes('message sent')
          )
        );
        if (hasOutreachMessage) {
          setOutreachLoading({});
        }
      }

      processedSystemMsgCountRef.current = onSystemMessages.length;
    }
  }, [onSystemMessages, systemMessageToken, lastResponseMode]);

  // Inject transition pill directly into messages (separate from systemMessages flow)
  useEffect(() => {
    if (!transitionPill || !transitionPillToken) return;
    if (lastPillTokenRef.current === transitionPillToken) return;
    lastPillTokenRef.current = transitionPillToken;

    setMessages((prev) => [...prev, {
      id: prev.length + 1,
      sender: 'agent',
      message: transitionPill,
      timestamp: new Date().toISOString(),
    }]);
  }, [transitionPill, transitionPillToken]);

  // Add single system message from backend when provided (backward compatibility)
  useEffect(() => {
    if (onSystemMessage && onSystemMessage.trim() && (!onSystemMessages || onSystemMessages.length === 0)) {
      setMessages((prev) => {
        // Avoid duplicates - check if this message already exists anywhere in chat
        const exists = prev.some(
          (m) =>
            m.sender === 'agent' &&
            typeof m.message === 'string' &&
            m.message.trim() === onSystemMessage.trim()
        );
        if (exists) {
          return prev;
        }
        const systemMessage = {
          id: prev.length + 1,
          sender: 'agent',
          message: onSystemMessage,
          timestamp: new Date().toISOString(),
        };
        return [...prev, systemMessage];
      });
    }
  }, [onSystemMessage, onSystemMessages]);

  const handleSendMessage = (message, mode, options = {}) => {
    const trimmed = (message || '').trim();
    console.warn('[QFTRACE:handleSendMessage]', {
      message: String(message || '').slice(0, 60),
      mode,
      trimmedLen: trimmed.length,
      jobPostingPhase,
      onUserQueryType: typeof onUserQuery,
    });

    // Allow empty messages when in channel selection phase (user pressed Enter after selecting channels)
    if (!trimmed && jobPostingPhase !== 'channel_selection') {
      console.warn('[QFTRACE:handleSendMessage:return_empty]');
      return;
    }

    if (/^(edit_template|edit template|edit_message|edit message)$/i.test(trimmed)) {
      openInlineTemplateEditor();
      setInputMessage('');
      return;
    }

    const activeInterviewCreationMessage = [...messages]
      .reverse()
      .find((msg) => msg?.metadata?.type === 'interview_creation');
    if (activeInterviewCreationMessage && trimmed) {
      const token = Date.now();
      setUserScrolledUp(false);
      setInputMessage('');
      setMessages((prev) => [
        ...prev,
        {
          id: `interview_creation_reply_${token}`,
          sender: 'user',
          message: trimmed,
          timestamp: new Date().toISOString(),
        },
      ]);
      setInterviewCreationInputs((prev) => ({
        ...prev,
        [activeInterviewCreationMessage.id]: { text: trimmed, token },
      }));
      return;
    }

    // If a follow-up warning is active, intercept ANY user-typed message and route to agent with candidate context.
    // Note: pendingFollowupRef is cleared by dismissFollowupMsg before "Send Anyway" calls this function,
    // so programmatic calls from that button are never intercepted here.
    if (pendingFollowupRef.current && trimmed) {
      const { msgId, candidate } = pendingFollowupRef.current;
      pendingFollowupRef.current = null;
      if (scrollDebounceRef.current) clearTimeout(scrollDebounceRef.current);
      setUserScrolledUp(false);
      setInputMessage('');
      // Remove warning bubble + add user's typed message in one atomic update
      setMessages(prev => {
        const filtered = prev.filter(m => m.id !== msgId);
        return [...filtered, { id: filtered.length + 1, sender: 'user', message: trimmed, timestamp: new Date().toISOString() }];
      });
      // Send to agent: follow-up intent for the specific candidate + user's typed note
      const agentMsg = `${getCandidateActionCommand('follow_up', candidate)} User note: "${trimmed}"`;
      if (typeof onUserQuery === 'function') onUserQuery(agentMsg, 'agent');
      return;
    }

    // Reset scroll position so new messages are visible
    if (scrollDebounceRef.current) clearTimeout(scrollDebounceRef.current);
    setUserScrolledUp(false);

    const nextId = messages.length + 1;
    const userMessage = {
      id: nextId,
      sender: 'user',
      message: trimmed || '', // Allow empty message
      timestamp: new Date().toISOString(),
      mode: mode || undefined, // "agent", "ask", or undefined (auto-detect)
    };

    // Add user message immediately (even if empty)
    setMessages([...messages, userMessage]);

    // Clear input after sending
    setInputMessage('');

    // Notify parent so it can send to backend for analysis
    // Backend will analyze and return system message if fields are missing
    if (typeof onUserQuery === 'function') {
      console.warn('[QFTRACE:handleSendMessage:calling_onUserQuery]');
      // Send empty string explicitly when trimmed is empty (for channel selection)
      onUserQuery(trimmed === '' ? '' : trimmed, mode, options);
    } else {
      console.warn('[QFTRACE:handleSendMessage:onUserQuery_not_a_function]', typeof onUserQuery);
    }
  };

  const handleSaveDraftTemplateEdit = () => {
    const updatedTemplate = draftTemplateEditor.value.trim();
    if (!updatedTemplate) {
      setDraftTemplateEditor((prev) => ({ ...prev, error: 'Template cannot be empty.' }));
      return;
    }

    setDraftTemplateEditor({ open: false, messageId: null, value: '', error: '' });
    setUserScrolledUp(false);
    setMessages((prev) => [
      ...prev,
      {
        id: prev.length + 1,
        sender: 'user',
        message: 'Template updated',
        timestamp: new Date().toISOString(),
        mode: 'agent',
      },
    ]);

    if (typeof onUserQuery === 'function') {
      onUserQuery(`__OUTREACH_TEMPLATE_REPLACE__\n${updatedTemplate}`, 'agent');
    }
  };

  // -----------------------------------------------------------------------
  // JD Upload flow handlers
  // -----------------------------------------------------------------------
  const openJdUploadModal = () => {
    // Directly open OS file picker — modal opens only after file is selected
    if (jdFileInputRef.current) {
      jdFileInputRef.current.value = '';
      jdFileInputRef.current.click();
    }
  };

  const handleJdFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setJdUploadModal({ isOpen: true, step: 'uploading', file, jdText: '', missingFields: [], fieldValues: {}, fieldIndex: 0, error: '' });
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await aiAutomationApi.postForm('/jd/upload-document', formData);
      if (res?.success) {
        setJdUploadModal(prev => ({
          ...prev,
          step: res.missing_fields?.length > 0 ? 'filling' : 'review',
          jdText: res.jd_text || '',
          missingFields: res.missing_fields || [],
          fieldIndex: 0,
          fieldValues: {},
          error: '',
        }));
      } else {
        setJdUploadModal(prev => ({ ...prev, step: 'pick', error: res?.detail || 'Upload failed. Please try again.' }));
      }
    } catch (err) {
      setJdUploadModal(prev => ({ ...prev, step: 'pick', error: err?.message || 'Upload failed. Please try again.' }));
    }
    // Reset file input so same file can be re-selected
    if (jdFileInputRef.current) jdFileInputRef.current.value = '';
  };

  const handleJdFieldNext = () => {
    setJdUploadModal(prev => {
      const nextIndex = prev.fieldIndex + 1;
      if (nextIndex >= prev.missingFields.length) {
        return { ...prev, step: 'review', fieldIndex: nextIndex };
      }
      return { ...prev, fieldIndex: nextIndex };
    });
  };

  const handleJdSubmit = () => {
    const { jdText, missingFields, fieldValues } = jdUploadModal;
    // Append filled-in field values to JD text
    let enrichedJd = jdText.trim();
    const additions = missingFields
      .filter(f => fieldValues[f.key]?.trim())
      .map(f => `${f.label}: ${fieldValues[f.key].trim()}`);
    if (additions.length > 0) {
      enrichedJd += `\n\n--- Additional Details ---\n${additions.join('\n')}`;
    }
    setJdUploadModal({ isOpen: false, step: 'pick', file: null, jdText: '', missingFields: [], fieldValues: {}, fieldIndex: 0, error: '' });
    // Send enriched JD text to the chat as if user typed it
    handleSendMessage(`Create a JD from this document:\n\n${enrichedJd}`, 'agent');
  };

  // Handler for meeting analysis - open modal
  const handleMeetingAnalysis = async (candidate) => {
    console.log('[handleMeetingAnalysis] Opening modal for:', candidate);
    if (!candidate) return;

    setMeetingAnalysisModal({
      isOpen: true,
      loading: true,
      candidate,
      analysis: null,
      aiResult: null,
      error: null,
    });

    try {
      const candidateEmail = String(candidate.email || candidate.candidate_email || '').trim();
      if (!candidateEmail) {
        throw new Error('Candidate email not found');
      }

      const metadata = getMetadata?.() || {};
      const companyId = getCid() || '';
      const userId = metadata.uid || metadata.userId || metadata.id || '';
      
      const resolvedSearchId = 
        candidate._searchId ||
        candidate.search_id ||
        candidate.searchId ||
        selectedJobId ||
        '';
      
      const resolvedSessionId =
        candidate.session_id ||
        candidate.sessionId ||
        selectedJobId ||
        '';
      
      const resolvedCandidateId = candidate.id || candidate.candidate_id || '';
      const scopedInterviewId = candidate.interview_id || candidate.interviewId || '';
      const scopedMeetingId = candidate.ai_note_taker_meeting_id || candidate.meeting_id || candidate.meetingId || '';

      // AI interviews are the authoritative "meeting analysis". Resolve the candidate's AI interview
      // from the calendar (the same source the interview workspace uses) and show its scored result
      // directly. The notes endpoint frequently returns a human/note-taker record whose id makes
      // /ai-result 400 — which is what dropped us into the "note-taker meeting_id not found" path
      // even though the candidate completed an AI interview.
      try {
        const _calParams = new URLSearchParams();
        if (companyId) _calParams.set('cid', String(companyId));
        if (userId) _calParams.set('user_id', String(userId));
        if (resolvedSearchId) _calParams.set('search_id', String(resolvedSearchId));
        _calParams.set('candidate_email', candidateEmail);
        const _calData = await aiAutomationApi.get(`/interviews/calendar/all?${_calParams.toString()}`);
        const _aiInterview = (Array.isArray(_calData?.interviews) ? _calData.interviews : [])
          .filter((iv) => String(iv?.candidate_email || '').toLowerCase() === candidateEmail.toLowerCase())
          .filter((iv) => !resolvedSearchId || String(iv.search_id || '') === String(resolvedSearchId))
          .filter((iv) => String(iv?.interview_type || '').toLowerCase() === 'ai' || iv?.ai_launch_token || iv?.ai_interview_id)
          .sort((a, b) => {
            const at = new Date(`${a?.scheduled_date || ''}T${a?.scheduled_time || '00:00:00'}`).getTime() || 0;
            const bt = new Date(`${b?.scheduled_date || ''}T${b?.scheduled_time || '00:00:00'}`).getTime() || 0;
            return bt - at;
          })[0];
        if (_aiInterview?.id) {
          // This candidate's meeting analysis IS the AI interview result — never fall back to a
          // (possibly stale/mismatched) human note-taker analysis if it can't be loaded.
          let _aiResult = null;
          try { _aiResult = await aiAutomationApi.get(`/interviews/${encodeURIComponent(String(_aiInterview.id))}/ai-result`); } catch { _aiResult = null; }
          if (_aiResult && (_aiResult.evaluation || _aiResult.transcript_text || _aiResult.transcript || _aiResult.metadata)) {
            setMeetingAnalysisModal((prev) => ({ ...prev, loading: false, aiResult: _aiResult, analysis: null, error: null }));
          } else {
            setMeetingAnalysisModal((prev) => ({ ...prev, loading: false, aiResult: null, analysis: null, error: "The AI interview result isn't available for this candidate yet." }));
          }
          return;
        }
      } catch { /* calendar lookup failed — fall through to the notes-based path */ }

      const requestCandidateNotes = async ({
        searchId = resolvedSearchId,
        interviewId = scopedInterviewId,
        meetingId = scopedMeetingId,
        includeSession = true,
        includeCandidate = true,
      } = {}) => {
        const params = new URLSearchParams();
        if (searchId) params.set('search_id', String(searchId));
        if (includeSession && resolvedSessionId) params.set('session_id', String(resolvedSessionId));
        if (includeCandidate && resolvedCandidateId) params.set('candidate_id', String(resolvedCandidateId));
        if (companyId) params.set('cid', String(companyId));
        if (userId) params.set('user_id', String(userId));
        if (interviewId) params.set('interview_id', String(interviewId));
        if (meetingId) params.set('meeting_id', String(meetingId));
        const query = params.toString();
        const data = await aiAutomationApi.get(
          `/interviews/candidate/${encodeURIComponent(candidateEmail)}/notes${query ? `?${query}` : ''}`
        );
        return Array.isArray(data?.interviews) ? data.interviews : [];
      };

      const canOpenInterviewNotes = (interview) => {
        if (!interview) return false;
        const noteTakerStatus = String(interview.ai_note_taker_status || '').trim().toLowerCase();
        if (['completed', 'recording_completed', 'ended', 'left', 'done', 'finished', 'disconnected', 'closed'].includes(noteTakerStatus)) {
          return Boolean(interview.ai_note_taker_meeting_id || interview.notes_cached_at || interview.meeting_completed);
        }

        if (interview.meeting_completed && (interview.notes_cached_at || interview.ai_note_taker_meeting_id)) {
          return true;
        }

        const interviewStatus = String(interview.status || '').trim().toLowerCase();
        return interviewStatus === 'completed' && (interview.notes_cached_at || interview.ai_note_taker_meeting_id);
      };

      let interviewNotes = await requestCandidateNotes();

      if (interviewNotes.length === 0 && resolvedSearchId) {
        interviewNotes = await requestCandidateNotes({
          searchId: resolvedSearchId,
          includeSession: false,
          includeCandidate: false,
          interviewId: '',
          meetingId: '',
        });
      }

      if (interviewNotes.length === 0 && scopedInterviewId) {
        const data = await aiAutomationApi.get(`/interviews/${encodeURIComponent(String(scopedInterviewId))}/notes`);
        if (data?.interview_id) {
          interviewNotes = [data];
        }
      }

      if (interviewNotes.length === 0) {
        // Scope by candidate_email ONLY. Passing cid + user_id makes /interviews/calendar/all run its
        // `EXISTS(automation_email_sync_history WHERE company_id=$cid AND user_id=$uid)` gate, which
        // drops a completed AI interview whose search has no matching sync_history row for this user —
        // the exact reason the report shows "No meeting analysis available" on the server while the
        // overview drawer (which calls calendar/all with NO params) renders it fine. candidate_email is
        // a plain filter that doesn't trigger that gate; we pick the completed interview client-side below.
        const query = new URLSearchParams();
        query.set('candidate_email', candidateEmail);

        const calendarData = await aiAutomationApi.get(`/interviews/calendar/all${query.toString() ? `?${query.toString()}` : ''}`);
        const calendarInterviews = Array.isArray(calendarData?.interviews) ? calendarData.interviews : [];
        const fallbackInterview = calendarInterviews
          .filter((interview) => String(interview?.candidate_email || '').toLowerCase() === candidateEmail.toLowerCase())
          .filter((interview) => {
            if (canOpenInterviewNotes(interview)) return true;
            const interviewStatus = String(interview?.status || '').trim().toLowerCase();
            const noteTakerStatus = String(interview?.ai_note_taker_status || '').trim().toLowerCase();
            const hasInterviewContext = Boolean(interview?.id || interview?.ai_note_taker_meeting_id || interview?.google_meet_link);
            return (
              hasInterviewContext &&
              (
                interviewStatus === 'completed' ||
                interviewStatus === 'interviewed' ||
                ['completed', 'recording_completed', 'ended', 'left', 'done', 'finished', 'disconnected', 'closed'].includes(noteTakerStatus)
              )
            );
          })
          .sort((a, b) => {
            const aStamp = new Date(`${a?.scheduled_date || ''}T${a?.scheduled_time || '00:00:00'}`).getTime() || 0;
            const bStamp = new Date(`${b?.scheduled_date || ''}T${b?.scheduled_time || '00:00:00'}`).getTime() || 0;
            return bStamp - aStamp;
          })[0];

        if (fallbackInterview?.id) {
          // A completed AI interview has NO note-taker notes, so /notes is empty — but it DOES have a
          // scored /ai-result. Try that first, otherwise the AI interview shows "No meeting analysis
          // available" here even though the overview drawer renders it fine.
          try {
            const aiResult = await aiAutomationApi.get(`/interviews/${encodeURIComponent(String(fallbackInterview.id))}/ai-result`);
            if (aiResult && (aiResult.evaluation || aiResult.transcript_text || aiResult.transcript || aiResult.metadata)) {
              setMeetingAnalysisModal((prev) => ({ ...prev, loading: false, aiResult, analysis: null, error: null }));
              return;
            }
          } catch { /* not an AI interview (or no result yet) — fall back to note-taker notes below */ }
          const data = await aiAutomationApi.get(`/interviews/${encodeURIComponent(String(fallbackInterview.id))}/notes`);
          if (data?.interview_id) {
            interviewNotes = [data];
          }
        }
      }

      // Last resort: we still have no notes but a scoped interview id exists — a pure AI interview with
      // no note-taker notes. Surface its scored /ai-result rather than throwing "No meeting analysis".
      if (interviewNotes.length === 0 && scopedInterviewId) {
        try {
          const aiResult = await aiAutomationApi.get(`/interviews/${encodeURIComponent(String(scopedInterviewId))}/ai-result`);
          if (aiResult && (aiResult.evaluation || aiResult.transcript_text || aiResult.transcript || aiResult.metadata)) {
            setMeetingAnalysisModal((prev) => ({ ...prev, loading: false, aiResult, analysis: null, error: null }));
            return;
          }
        } catch { /* fall through to the throw below */ }
      }

      if (interviewNotes.length === 0) {
        throw new Error('No meeting analysis available');
      }

      // Prefer an AI-interview record when the candidate has one — its scored result is what we want
      // to show (a note flagged 'ai' / with a launch token / an ai_interview_id).
      const prioritizedNote =
        interviewNotes.find((note) => String(note?.interview_type || '').toLowerCase() === 'ai' || note?.ai_launch_token || note?.ai_interview_id) ||
        interviewNotes.find((note) => note?.meeting_analysis_json?.success) ||
        interviewNotes.find((note) => note?.meeting_completed || note?.notes_cached_at) ||
        interviewNotes[0];

      // The scored AI interview result is authoritative — ALWAYS try it first for the chosen
      // interview (the note's interview_type isn't always tagged 'ai'). Only if this is genuinely a
      // human interview does /ai-result return 400 and we fall through to the note-taker analysis.
      if (prioritizedNote?.interview_id) {
        try {
          const aiResult = await aiAutomationApi.get(`/interviews/${encodeURIComponent(String(prioritizedNote.interview_id))}/ai-result`);
          if (aiResult && (aiResult.evaluation || aiResult.transcript_text || aiResult.transcript || aiResult.metadata)) {
            setMeetingAnalysisModal((prev) => ({ ...prev, loading: false, aiResult, analysis: null, error: null }));
            return;
          }
        } catch { /* not an AI interview (or no result yet) — fall back to the note-taker path below */ }
      }

      let analysis = prioritizedNote?.meeting_analysis_json || null;
      console.log('[handleMeetingAnalysis] Initial analysis from note:', analysis);
      
      if ((!analysis || analysis.success === false) && prioritizedNote?.interview_id) {
        console.log('[handleMeetingAnalysis] Generating new analysis for interview:', prioritizedNote.interview_id);
        analysis = await aiAutomationApi.post(`/interviews/${encodeURIComponent(String(prioritizedNote.interview_id))}/notes/analyze`);
        console.log('[handleMeetingAnalysis] Generated analysis response:', analysis);
      }

      console.log('[handleMeetingAnalysis] Final analysis to save:', analysis);
      
      if (analysis) {
        console.log('[handleMeetingAnalysis] Setting modal state with analysis');
        setMeetingAnalysisModal(prev => ({
          ...prev,
          loading: false,
          analysis,
        }));
        console.log('[handleMeetingAnalysis] Modal state updated');
      } else {
        console.log('[handleMeetingAnalysis] No analysis available, throwing error');
        throw new Error('No meeting analysis available');
      }
    } catch (error) {
      console.error('[handleMeetingAnalysis] Error:', error);
      setMeetingAnalysisModal(prev => ({
        ...prev,
        loading: false,
        error: error?.response?.data?.message || error?.message || 'No meeting analysis available',
      }));
    }
  };

  // Auto-send message forwarded from dashboard and render it as a user chat message.
  useEffect(() => {
    const text = (autoSendMessage || '').trim();
    if (!text) return;
    if (!autoSendToken) return;
    if (lastAutoSendTokenRef.current === autoSendToken) return;
    lastAutoSendTokenRef.current = autoSendToken;
    handleSendMessage(text, undefined, {
      bypassLoadingGuard: true,
      ...(autoSendOptions && typeof autoSendOptions === 'object' ? autoSendOptions : {}),
    });
    if (typeof onAutoSendConsumed === 'function') {
      onAutoSendConsumed(autoSendToken);
    }
  }, [autoSendMessage, autoSendToken, autoSendOptions, onAutoSendConsumed]);

  // Keep chat input focused when this view is opened/restored.
  useEffect(() => {
    if (historyLoading) return;
    const timer = setTimeout(() => {
      if (!inputRef.current) return;
      inputRef.current.focus();
      const value = inputRef.current.value || '';
      if (typeof inputRef.current.setSelectionRange === 'function') {
        inputRef.current.setSelectionRange(value.length, value.length);
      }
    }, 0);

    return () => clearTimeout(timer);
  }, [historyLoading, selectedJobId, resetToken]);

  // --- In-chat follow-up flow ---
  // Shows a 48h cooldown warning in chat if needed; otherwise routes directly to the outreach agent.
  const startInlineFollowup = (candidateData, emailReplies) => {
    const sentMsgs = (emailReplies || []).filter(r => r.is_sent_email);
    if (sentMsgs.length > 0) {
      const latest = sentMsgs.reduce((acc, r) => {
        const d = new Date(r.sent_at || r.received_at || r.created_at || 0);
        return d > acc ? d : acc;
      }, new Date(0));
      if ((Date.now() - latest.getTime()) / 3600000 < 48) {
        const msgId = `inline-fu-${Date.now()}`;
        pendingFollowupRef.current = { msgId, candidate: candidateData };
        setMessages(prev => [...prev, {
          id: msgId,
          sender: 'agent',
          message: '',
          timestamp: new Date().toISOString(),
          metadata: { type: 'inline_followup', step: 'warning', candidate: candidateData, lastSentAt: latest },
        }]);
        return;
      }
    }
    // No cooldown issue — send command directly to outreach agent
    handleSendMessage(getCandidateActionCommand('follow_up', candidateData), 'agent');
  };

  const dismissFollowupMsg = (msgId) => {
    if (pendingFollowupRef.current?.msgId === msgId) pendingFollowupRef.current = null;
    setMessages(prev => prev.filter(m => m.id !== msgId));
  };

 const renderInlineCandidateCard = (cvc, cardKey = 'candidate-card') => {
    if (!cvc) return null;

    // Hide the card once this candidate has been rejected (removed from the job).
    const _rejId = cvc.id ?? cvc.candidate_id;
    const _rejEmail = String(cvc.email || cvc.candidate_email || '').toLowerCase();
    if ((_rejId != null && rejectedKeys.has(`id:${_rejId}`)) || (_rejEmail && rejectedKeys.has(`email:${_rejEmail}`))) {
      return null;
    }

    // Look up latest candidate data from candidates array instead of using static message metadata
    const candidateId = cvc.id || cvc.candidate_id || null;
    const latestCandidate = candidates.find((candidateItem) => {
      const currentId = candidateItem?.id || candidateItem?.candidate_id || null;
      if (candidateId && currentId && String(currentId) === String(candidateId)) return true;
      if (cvc.email) {
        return (candidateItem?.email || '').toLowerCase() === (cvc.email || '').toLowerCase();
      }
      return false;
    }) || null;
    const candidateData = {
      ...(latestCandidate || cvc),
      search_id: (latestCandidate || cvc)?.search_id || (latestCandidate || cvc)?.searchId || (latestCandidate || cvc)?._searchId || effectiveSyncSearchId || selectedJobId || '',
    };

    const cvcMatchColor = Number(candidateData.match_percentage) >= 80 ? '#22C55E' : Number(candidateData.match_percentage) >= 60 ? '#F59E0B' : '#EF4444';
    const sourceTypeLabel = getSourceTypeLabel(candidateData.source_type, candidateData.platforms) || candidateData.source_type || '';
    const resumeLink = getResumeLink(candidateData);
    const scheduledInterview = getScheduledInterviewInfo(candidateData, interviewsByCandidateKey);
    const interviewLockInfo = getCandidateInterviewLockInfo(candidateData, interviewsByCandidateKey, !candidateData.search_id);
    const hasCompletedInterview = Boolean(scheduledInterview && scheduledInterview.lifecycle === 'completed');
    const hasScheduledInterview = Boolean(scheduledInterview && scheduledInterview.lifecycle === 'scheduled');
    const meetingDateLabel = formatInterviewDateLabel(scheduledInterview?.scheduled_date);
    const meetingTimeLabel = formatInterviewTimeLabel(scheduledInterview?.scheduled_time);
    // Lazy-loaded email data for this card
    const emailData = cardEmailData[cardKey] || { loading: false, replies: [] };
    const emailReplies = emailData.replies || [];
    const emailOutreachCount = emailReplies.filter(r => r.is_sent_email).length;
    const emailRepliesCount = emailReplies.filter(r => !r.is_sent_email).length;
    const hasEmailOutreach = emailOutreachCount > 0;
    const hasResume = hasCandidateResume(candidateData);
    const normalizedStatus = normalizeCandidateStatus(candidateData);

    // Override action plan: if we know outreach was sent (via email API), skip "send_outreach"
    const rawActionPlan = getCandidateActionPlan(candidateData, interviewsByCandidateKey, { hasEmailOutreach, hasResume, interviewLockInfo });
    const actionPlanWithFeatureAccess = rawActionPlan.filter((action) => {
      if (action.key === 'send_outreach' || action.key === 'follow_up' || action.key === 'request_resume') {
        return canUseOutreach;
      }
      return true;
    });
    const actionPlan = hasEmailOutreach && rawActionPlan.some(a => a.key === 'send_outreach')
      ? actionPlanWithFeatureAccess.filter(a => a.key !== 'send_outreach')
      : actionPlanWithFeatureAccess;

    let computedExpYears = candidateData.experience_years || candidateData.total_experience || null;
    if (!computedExpYears && candidateData.experience) {
      let expArr = candidateData.experience;
      if (typeof expArr === 'string') { try { expArr = JSON.parse(expArr); } catch { expArr = []; } }
      if (Array.isArray(expArr)) {
        let totalMonths = 0;
        expArr.forEach(exp => {
          if (exp.duration) {
            const yMatch = exp.duration.match(/(\d+)\s*y/i);
            const mMatch = exp.duration.match(/(\d+)\s*m/i);
            if (yMatch) totalMonths += parseInt(yMatch[1], 10) * 12;
            if (mMatch) totalMonths += parseInt(mMatch[1], 10);
          }
        });
        if (totalMonths > 0) computedExpYears = Math.round(totalMonths / 12 * 10) / 10;
      }
    }

    // Get or initialize accordion state for this card
    const cardState = candidateCardStates[cardKey] || {
      engagement: false,
      nextStep: false,
      emailTimeline: false,
      interviewHistory: false,
      funnelStage: false,
      conversationHistory: false,
      internalNotes: false,
    };

    const candidateRecordId = cvc.id || cvc.candidate_id || cardKey;
    const currentNote = candidateNotes[candidateRecordId] || cvc.notes || '';

    const handleNoteChange = (e) => {
      setCandidateNotes(prev => ({
        ...prev,
        [candidateRecordId]: e.target.value
      }));
    };

    const saveNote = () => {
      // Save to localStorage for persistence
      try {
        localStorage.setItem(`candidate_note_${candidateRecordId}`, candidateNotes[candidateRecordId] || '');
      } catch (e) {
        console.warn('Failed to save note to localStorage:', e);
      }
    };

    const toggleConversationHistory = () => {
      // If opening and not yet loaded, fetch emails from API
      const isCurrentlyOpen = cardState.conversationHistory;
      if (!isCurrentlyOpen && candidateData.email && !cardEmailData[cardKey]) {
        setCardEmailData(prev => ({ ...prev, [cardKey]: { loading: true, replies: [] } }));
        const resolvedSearchId = candidateData._searchId || candidateData.search_id || candidateData.searchId || selectedJobId || '';
        const resolvedCandidateId = candidateData.id || candidateData.candidate_id || '';
        const params = new URLSearchParams();
        if (resolvedSearchId) params.set('search_id', resolvedSearchId);
        if (resolvedCandidateId) params.set('candidate_id', String(resolvedCandidateId));
        aiAutomationApi.get(`/email/replies/candidate/${encodeURIComponent(candidateData.email)}${params.toString() ? `?${params.toString()}` : ''}`)
          .then(data => {
            setCardEmailData(prev => ({ ...prev, [cardKey]: { loading: false, replies: data?.replies || [] } }));
          })
          .catch(() => {
            setCardEmailData(prev => ({ ...prev, [cardKey]: { loading: false, replies: [] } }));
          });
      }
      toggleSection('conversationHistory');
    };

    const toggleSection = (section) => {
      setCandidateCardStates(prev => ({
        ...prev,
        [cardKey]: {
          ...(prev[cardKey] || cardState),
          [section]: !(prev[cardKey]?.[section] ?? cardState[section])
        }
      }));
    };

    const AccordionItem = ({ title, icon, isExpanded, onToggle, children }) => (
      <div className="border-b" style={{ borderColor: isDark ? '#374151' : '#E5E7EB' }}>
        <button
          onClick={onToggle}
          className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        >
          <span className="flex items-center gap-2 text-xs font-semibold" style={{ color: isDark ? '#D1D5DB' : '#4B5563' }}>
            {icon}
            {title}
          </span>
          <svg
            className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            style={{ color: isDark ? '#9CA3AF' : '#6B7280' }}
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {isExpanded && (
          <div className="px-3 py-2 text-xs" style={{ color: isDark ? '#D1D5DB' : '#4B5563' }}>
            {children}
          </div>
        )}
      </div>
    );

    return (
      <div key={cardKey} className="flex justify-start mb-4" style={{ paddingLeft: '16px', paddingRight: '16px' }}>
        <div className="w-full rounded-2xl overflow-hidden shadow-xl" style={{ backgroundColor: isDark ? '#1F2937' : '#FFFFFF', border: '1px solid ' + (isDark ? '#374151' : '#E5E7EB'), maxWidth: '100%' }}>
          <div className="flex items-center justify-between px-4 py-3" style={{ background: isDark ? 'linear-gradient(135deg, ' + primaryColor + '20, ' + (secondaryColor || primaryColor) + '10)' : 'linear-gradient(135deg, ' + primaryColor + '08, ' + (secondaryColor || primaryColor) + '04)', borderBottom: '1px solid ' + (isDark ? '#374151' : '#E5E7EB') }}>
            <div className="flex items-center gap-3 min-w-0">
              <CandidateAvatar name={cvc.name} index={cardKey.length} size={48} />
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <h4 className="text-sm font-bold truncate" style={{ color: isDark ? '#F9FAFB' : '#111827' }}>{cvc.name || 'Candidate'}</h4>
                  {sourceTypeLabel && <CandidateSourceIcon source={sourceTypeLabel} />}
                </div>
                <div className="flex items-center gap-2 flex-wrap mt-1">
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold flex-shrink-0" style={{ backgroundColor: primaryColor + '15', color: primaryColor }}>
                    {candidateData.funnel_stage || candidateData.status || candidateData.candidate_status || (emailOutreachCount > 0 ? 'Contacted' : 'Sourced')}
                  </span>
                  {cvc.location && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0" style={{ backgroundColor: isDark ? '#1E3A5F' : '#DBEAFE', color: isDark ? '#93C5FD' : '#1E40AF' }}>
                      <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {cvc.location}
                    </span>
                  )}
                  {cvc.email && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0" style={{ backgroundColor: isDark ? '#3F2E2E' : '#FEE2E2', color: isDark ? '#FCA5A5' : '#DC2626' }}>
                      <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      {cvc.email}
                    </span>
                  )}
                  {computedExpYears && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0" style={{ backgroundColor: isDark ? '#1C2E1C' : '#DCFCE7', color: isDark ? '#86EFAC' : '#166534' }}>{computedExpYears}y exp</span>
                  )}
                </div>
                {candidateData.title && <p className="text-[10px] truncate mt-0.5" style={{ color: isDark ? '#9CA3AF' : '#6B7280' }}>{candidateData.title}{candidateData.company ? ' at ' + candidateData.company : ''}</p>}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Social Icons */}
              {candidateData.linkedin && (
                <a href={candidateData.linkedin} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full flex items-center justify-center transition-all hover:opacity-80" style={{ backgroundColor: isDark ? '#1E3A5F' : '#DBEAFE' }}>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" style={{ color: isDark ? '#93C5FD' : '#1E40AF' }}><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                </a>
              )}
              {candidateData.github && (
                <a href={candidateData.github} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full flex items-center justify-center transition-all hover:opacity-80" style={{ backgroundColor: isDark ? '#374151' : '#F3F4F6' }}>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" style={{ color: isDark ? '#D1D5DB' : '#4B5563' }}><path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/></svg>
                </a>
              )}
              {candidateData.match_percentage && Number(candidateData.match_percentage) > 0 && (
                <span className="inline-flex items-center gap-1">
                  <span
                    className="text-[10px] font-bold px-2 py-1 rounded"
                    style={{ color: cvcMatchColor, backgroundColor: isDark ? cvcMatchColor + '20' : cvcMatchColor + '10' }}
                    title="Resume/JD match score. The AI interview result score is shown inside Meeting Analysis."
                  >
                    {candidateData.match_percentage}% resume match
                  </span>
                  <ScoreDetailsInfo details={candidateData.score_details || candidateData.scoreDetails} candidateName={candidateData.name || candidateData.candidate_name || candidateData.full_name} title="Resume Match Details" />
                </span>
              )}
              {/* Priority Star */}
              {onMarkPriority && (
                <button
                  onClick={() => onMarkPriority(candidateData)}
                  className="w-8 h-8 rounded-full flex items-center justify-center transition-all hover:scale-110"
                  style={{ backgroundColor: isDark ? '#374151' : '#F3F4F6' }}
                  title="Toggle Priority"
                >
                  <svg className="w-4 h-4" fill={candidateData.priority ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24" style={{ color: candidateData.priority ? '#F59E0B' : '#9CA3AF' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                  </svg>
                </button>
              )}
              {/* Full Details Button */}
              {onViewCandidateDetails && (
                <button
                  onClick={() => onViewCandidateDetails(cvc)}
                  className="px-3 py-1.5 rounded-lg text-[10px] font-semibold transition-all hover:shadow-md border"
                  style={{ borderColor: primaryColor, color: primaryColor, backgroundColor: 'transparent' }}
                >
                  Full Details
                </button>
              )}
            </div>
          </div>

          <div className="px-3 py-2 space-y-2">
            {/* Current Funnel Stage */}
            <div className="rounded-lg p-1.5" style={{ backgroundColor: isDark ? primaryColor + '15' : primaryColor + '08', border: '1px solid ' + (isDark ? primaryColor + '30' : primaryColor + '20') }}>
              <p className="text-[10px] font-semibold uppercase tracking-wider mb-1 flex items-center gap-1" style={{ color: primaryColor }}>
                <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/></svg>
                Current Funnel Stage
              </p>
              <div className="flex items-center gap-1.5 flex-wrap text-xs">
                {(() => {
                  const rawStatus = candidateData.funnel_stage || candidateData.status || candidateData.candidate_status || 'Sourced';
                  const currentStatus = String(rawStatus).toLowerCase().trim().replace(/[^a-z]/g, '');
                  // Map variations to standard stage names
                  const statusMap = {
                    'interview': 'interviewed',
                    'identified': 'sourced',
                    'sourced': 'sourced',
                    'contacted': 'contacted',
                    'interested': 'interested',
                    'shortlisted': 'shortlisted',
                    'interviewed': 'interviewed'
                  };
                  const normalizedStatus = statusMap[currentStatus] || currentStatus;
                  const stages = ['sourced', 'contacted', 'interested', 'shortlisted', 'interviewed'];
                  const stageLabels = ['Sourced', 'Contacted', 'Interested', 'Shortlisted', 'Interviewed'];
                  return stages.map((stage, idx) => {
                    const isCurrent = stage === normalizedStatus;
                    return (
                      <React.Fragment key={stage}>
                        <span
                          className="px-1.5 py-0.5 rounded font-medium text-[10px]"
                          style={{
                            backgroundColor: isCurrent ? primaryColor + '30' : 'transparent',
                            color: isCurrent ? primaryColor : (isDark ? '#9CA3AF' : '#6B7280'),
                            fontWeight: isCurrent ? 600 : 400
                          }}
                        >
                          {stageLabels[idx]}
                        </span>
                        {idx < stages.length - 1 && (
                          <span style={{ color: isDark ? '#9CA3AF' : '#6B7280' }}>→</span>
                        )}
                      </React.Fragment>
                    );
                  });
                })()}
              </div>
            </div>

            {/* Contact Information - Only show if details are present */}
            {(cvc.email || cvc.phone || cvc.company) && (
              <div className="rounded-lg p-1.5 space-y-1" style={{ backgroundColor: isDark ? '#111827' : '#F9FAFB', color: isDark ? '#D1D5DB' : '#4B5563' }}>
                <p className="text-[10px] font-semibold uppercase tracking-wider mb-1 flex items-center gap-1" style={{ color: isDark ? '#9CA3AF' : '#6B7280' }}>
                  <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
                  Contact Information
                </p>
                {cvc.email && <div className="flex items-center gap-1.5"><svg className="w-3 h-3 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg><span className="text-xs">{cvc.email}</span></div>}
                {cvc.phone && <div className="flex items-center gap-1.5"><svg className="w-3 h-3 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg><span className="text-xs">{cvc.phone}</span></div>}
                {cvc.company && <div className="flex items-center gap-1.5"><svg className="w-3 h-3 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/></svg><span className="text-xs">{cvc.company}</span></div>}
              </div>
            )}

            {/* Candidate Details */}
            <div className="rounded-lg p-1.5 space-y-0.5" style={{ backgroundColor: isDark ? '#111827' : '#F9FAFB', color: isDark ? '#D1D5DB' : '#4B5563' }}>
              <div className="flex items-center gap-1.5"><svg className="w-2.5 h-2.5 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg><span className="text-xs">Current Role: {cvc.title || cvc.job_title || cvc.current_role || cvc.position || 'Not provided'}</span></div>
              <div className="flex items-center gap-1.5"><svg className="w-2.5 h-2.5 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/></svg><span className="text-xs">Current Company: {cvc.company || cvc.current_company || cvc.organization || 'Not provided'}</span></div>
              <div className="flex items-center gap-1.5"><svg className="w-2.5 h-2.5 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg><span className="text-xs">Experience: {computedExpYears ? computedExpYears + ' years' : 'Not provided'}</span></div>
            </div>

            {/* Top Skills */}
            {(() => {
              const skillsList = normalizeProfileList(cvc.skills).map(getProfileListLabel).filter(Boolean);
              return (
                <div className="rounded-lg p-1.5" style={{ backgroundColor: isDark ? primaryColor + '15' : primaryColor + '08', border: '1px solid ' + (isDark ? primaryColor + '30' : primaryColor + '20') }}>
                  <p className="text-[10px] font-semibold uppercase tracking-wider mb-1 flex items-center gap-1" style={{ color: primaryColor }}>
                    <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
                    Top Skills
                  </p>
                  {skillsList.length > 0 ? (
                    <div className="flex flex-wrap gap-0.5">
                      {skillsList.slice(0, 6).map((skill, i) => (
                        <span key={i} className="px-1.5 py-0.5 rounded-full text-[11px] font-medium" style={{ backgroundColor: isDark ? primaryColor + '30' : primaryColor + '15', color: primaryColor, border: isDark ? '1px solid ' + primaryColor + '40' : '1px solid ' + primaryColor + '30' }}>{skill}</span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-xs" style={{ color: isDark ? '#9CA3AF' : '#6B7280' }}>No skills listed</span>
                  )}
                </div>
              );
            })()}

            {/* Engagement Summary */}
            <AccordionItem
              title="Engagement Summary"
              icon={<svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/></svg>}
              isExpanded={cardState.engagement}
              onToggle={() => toggleSection('engagement')}
            >
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-[10px] font-bold" style={{ color: primaryColor }}>{hasEmailOutreach ? emailOutreachCount : (cvc.outreach_sent || 0)}</span>
                <span className="text-xs" style={{ color: isDark ? '#9CA3AF' : '#6B7280' }}>Outreach</span>
                <span className="text-gray-300">•</span>
                <span className="text-[10px] font-bold" style={{ color: isDark ? '#93C5FD' : '#1E40AF' }}>{hasEmailOutreach ? emailRepliesCount : (cvc.replies || 0)}</span>
                <span className="text-xs" style={{ color: isDark ? '#9CA3AF' : '#6B7280' }}>Replies</span>
                <span className="text-gray-300">•</span>
                <span className="text-[10px] font-bold" style={{ color: isDark ? '#86EFAC' : '#166534' }}>{hasResume ? '✓' : '—'}</span>
                <span className="text-xs" style={{ color: isDark ? '#9CA3AF' : '#6B7280' }}>Resume</span>
                <span className="text-gray-300">•</span>
                <span className="text-[10px] font-bold" style={{ color: isDark ? '#FCD34D' : '#92400E' }}>{cvc.response_time || '—'}</span>
                <span className="text-xs" style={{ color: isDark ? '#9CA3AF' : '#6B7280' }}>Response Time</span>
              </div>
            </AccordionItem>

            {/* Suggested Next Step */}
            <AccordionItem
              title="Suggested Next Step"
              icon={<svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/></svg>}
              isExpanded={cardState.nextStep}
              onToggle={() => toggleSection('nextStep')}
            >
              <p className="text-xs" style={{ color: isDark ? '#D1D5DB' : '#4B5563' }}>
                {hasCompletedInterview ? 'Interview completed — review meeting outcome or notes' :
                 hasScheduledInterview ? 'Interview scheduled — share or join the meeting details' :
                 normalizedStatus.includes('interest') && !hasResume ? 'Request Resume — candidate is interested but has not shared a resume' :
                 emailRepliesCount > 0 ? 'Schedule Interview — candidate has replied' :
                 emailOutreachCount > 0 ? 'Send Follow-up Email — awaiting candidate reply' :
                 cvc.funnel_stage?.toLowerCase() === 'sourced' || cvc.status === 'Sourced' ? 'Send Initial Outreach' :
                 cvc.funnel_stage?.toLowerCase() === 'contacted' || cvc.status === 'Contacted' ? 'Send Follow-up Email' :
                 cvc.funnel_stage?.toLowerCase() === 'interested' || cvc.status === 'Interested' ? 'Schedule Interview' :
                 cvc.funnel_stage?.toLowerCase() === 'shortlisted' || cvc.status === 'Shortlisted' ? 'Book Interview' :
                 'Send Initial Outreach'}
              </p>
            </AccordionItem>

            {/* Conversation History */}
            <AccordionItem
              title={`Conversation History${emailReplies.length > 0 ? ` (${emailReplies.length})` : ''}`}
              icon={<svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/></svg>}
              isExpanded={cardState.conversationHistory}
              onToggle={toggleConversationHistory}
            >
              {!candidateData.email ? (
                <div className="text-xs py-1" style={{ color: isDark ? '#9CA3AF' : '#6B7280' }}>No email address available</div>
              ) : emailData.loading ? (
                <div className="flex items-center gap-2 py-2">
                  <svg className="animate-spin w-3 h-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" style={{ color: primaryColor }}>
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  <span className="text-xs" style={{ color: isDark ? '#9CA3AF' : '#6B7280' }}>Loading…</span>
                </div>
              ) : emailReplies.length > 0 ? (
                <div className="space-y-1.5 mt-1">
                  {emailReplies.slice(0, 5).map((reply, ri) => {
                    const isSent = !!reply.is_sent_email;
                    const dateStr = reply.sent_at || reply.received_at || reply.created_at || '';
                    const dateLabel = dateStr ? new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
                    const subject = reply.subject || (isSent ? 'Outreach Sent' : 'Reply Received');
                    const preview = (reply.body || reply.content || '').replace(/\n/g, ' ').slice(0, 100);
                    return (
                      <div key={ri} className="rounded-md p-2 border-l-2" style={{
                        borderLeftColor: isSent ? primaryColor : (isDark ? '#86EFAC' : '#16A34A'),
                        backgroundColor: isDark ? '#111827' : '#F9FAFB'
                      }}>
                        <div className="flex items-center justify-between gap-1 flex-wrap">
                          <span className="text-[10px] font-semibold truncate" style={{ color: isDark ? '#E5E7EB' : '#111827' }}>{subject}</span>
                          <span className="inline-flex items-center px-1 py-0.5 rounded text-[9px] font-semibold flex-shrink-0" style={{
                            backgroundColor: isSent ? primaryColor + '20' : (isDark ? '#16311D' : '#DCFCE7'),
                            color: isSent ? primaryColor : (isDark ? '#86EFAC' : '#15803D')
                          }}>{isSent ? 'Sent' : 'Received'}</span>
                        </div>
                        {dateLabel && <p className="text-[10px] mt-0.5" style={{ color: isDark ? '#9CA3AF' : '#6B7280' }}>{dateLabel}</p>}
                        {preview && <p className="text-[10px] mt-0.5 line-clamp-2 leading-relaxed" style={{ color: isDark ? '#9CA3AF' : '#6B7280' }}>{preview}</p>}
                      </div>
                    );
                  })}
                  {emailReplies.length > 5 && (
                    <p className="text-[10px] text-center mt-1" style={{ color: primaryColor }}>+{emailReplies.length - 5} more — open Full Details to view all</p>
                  )}
                </div>
              ) : (
                <div className="text-xs py-1" style={{ color: isDark ? '#9CA3AF' : '#6B7280' }}>
                  {hasEmailOutreach ? 'Outreach sent — sync emails to load replies.' : 'No emails yet. Send outreach to start the conversation.'}
                </div>
              )}
            </AccordionItem>

            {/* Internal Notes */}
            <AccordionItem
              title="Internal Notes"
              icon={<svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/></svg>}
              isExpanded={cardState.internalNotes}
              onToggle={() => toggleSection('internalNotes')}
            >
              <div className="space-y-2">
                {currentNote ? (
                  <div>
                    <div className="text-xs p-1.5 rounded" style={{ backgroundColor: isDark ? '#1F2937' : '#FFFFFF', color: isDark ? '#D1D5DB' : '#4B5563' }}>
                      {currentNote}
                    </div>
                    <button
                      onClick={() => {
                        const note = prompt('Edit note:', currentNote);
                        if (note !== null) {
                          setCandidateNotes(prev => ({ ...prev, [candidateId]: note }));
                          try {
                            localStorage.setItem(`candidate_note_${candidateId}`, note);
                          } catch (e) {
                            console.warn('Failed to save note:', e);
                          }
                        }
                      }}
                      className="text-[10px] mt-0.5 hover:underline"
                      style={{ color: primaryColor }}
                    >
                      Edit
                    </button>
                  </div>
                ) : (
                  <div className="text-xs" style={{ color: isDark ? '#9CA3AF' : '#6B7280' }}>No notes yet</div>
                )}
                {noteInputVisible[candidateId] ? (
                  <div className="flex gap-1 mt-2">
                    <input
                      type="text"
                      placeholder="Add a note..."
                      value={candidateNotes[candidateId] || ''}
                      onChange={handleNoteChange}
                      className="flex-1 px-2 py-1 text-[10px] rounded border focus:outline-none focus:ring-1"
                      style={{
                        borderColor: isDark ? '#4B5563' : '#D1D5DB',
                        backgroundColor: isDark ? '#1F2937' : '#FFFFFF',
                        color: isDark ? '#E5E7EB' : '#1F2937'
                      }}
                      autoFocus
                      onBlur={() => {
                        saveNote();
                        setNoteInputVisible(prev => ({ ...prev, [candidateId]: false }));
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          saveNote();
                          setNoteInputVisible(prev => ({ ...prev, [candidateId]: false }));
                        }
                        if (e.key === 'Escape') {
                          setNoteInputVisible(prev => ({ ...prev, [candidateId]: false }));
                        }
                      }}
                    />
                  </div>
                ) : (
                  <button
                    onClick={() => setNoteInputVisible(prev => ({ ...prev, [candidateId]: true }))}
                    className="flex items-center gap-1 text-[10px] font-semibold transition-all hover:opacity-80"
                    style={{ color: primaryColor }}
                  >
                    <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Note
                  </button>
                )}
              </div>
            </AccordionItem>

            {/* Action Buttons */}
            <div className="flex gap-2 flex-wrap">
              {scheduledInterview && (
                <div
                  className="w-full rounded-lg px-3 py-2 flex items-center justify-between gap-3 flex-wrap"
                  style={{
                    backgroundColor: isDark ? `${primaryColor}18` : `${primaryColor}0D`,
                    border: `1px solid ${primaryColor}33`,
                  }}
                >
                  <div className="min-w-0">
                    <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: primaryColor }}>
                      {hasCompletedInterview ? 'Meeting Completed' : 'Meeting Scheduled'}
                    </p>
                    <p className="text-xs font-semibold mt-0.5" style={{ color: isDark ? '#F9FAFB' : '#111827' }}>
                      {[meetingDateLabel, meetingTimeLabel].filter(Boolean).join(' • ') || 'Time confirmed'}
                    </p>
                    {scheduledInterview.interviewer_name && (
                      <p className="text-[11px] mt-0.5" style={{ color: isDark ? '#D1D5DB' : '#4B5563' }}>
                        Interviewer: {scheduledInterview.interviewer_name}
                      </p>
                    )}
                  </div>
                  {!hasCompletedInterview && scheduledInterview.google_meet_link ? (
                    <a
                      href={scheduledInterview.google_meet_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 rounded-lg text-xs font-semibold transition-all hover:shadow-md"
                      style={{
                        background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor || primaryColor})`,
                        color: '#fff',
                      }}
                    >
                      Join Meet
                    </a>
                  ) : hasCompletedInterview ? (
                    <span
                      className="px-3 py-1.5 rounded-lg text-[11px] font-medium"
                      style={{
                        backgroundColor: isDark ? '#16311D' : '#DCFCE7',
                        color: isDark ? '#86EFAC' : '#166534',
                      }}
                    >
                      Completed
                    </span>
                  ) : (
                    <span
                      className="px-3 py-1.5 rounded-lg text-[11px] font-medium"
                      style={{
                        backgroundColor: isDark ? '#374151' : '#F3F4F6',
                        color: isDark ? '#D1D5DB' : '#4B5563',
                      }}
                    >
                      Join link pending
                    </span>
                  )}
                </div>
              )}

              {/* Send Follow-up — shown when outreach has been sent */}
              {hasEmailOutreach && (
                <button
                  onClick={() => startInlineFollowup(candidateData, emailReplies)}
                  className="px-4 py-2 rounded-lg text-xs font-semibold transition-all hover:shadow-md border-2"
                  style={{ borderColor: primaryColor, color: primaryColor, backgroundColor: 'transparent' }}
                >
                  Send Follow-up
                  {emailOutreachCount > 0 && (
                    <span className="ml-1.5 text-[10px] opacity-70">({emailOutreachCount} sent · {emailRepliesCount} replied)</span>
                  )}
                </button>
              )}

              {/* Generate AI Interview - only for manual interviews that still need question generation */}
              {onGenerateInterview && canGenerateManualInterviewQuestions(candidateData, interviewLockInfo) && (
                <button
                  onClick={() => onGenerateInterview(candidateData)}
                  className="px-4 py-2 rounded-lg text-xs font-semibold transition-all hover:shadow-md border-2"
                  style={{ borderColor: primaryColor, color: primaryColor, backgroundColor: 'transparent' }}
                >
                  📋 Generate AI Interview
                </button>
              )}

              {/* Meeting Analysis — only after the interview is actually completed (not merely at the
                  interview stage), so it can't be opened before there's anything to analyse. */}
              {hasCompletedInterview && (
                <button
                  onClick={() => handleMeetingAnalysis(candidateData)}
                  className="px-4 py-2 rounded-lg text-xs font-semibold transition-all hover:shadow-md border-2"
                  style={{ borderColor: primaryColor, color: primaryColor, backgroundColor: 'transparent' }}
                >
                  Meeting Analysis
                </button>
              )}
              {(resumeLink || (cvc.doc_id && onViewResume)) && (
                resumeLink ? (
                  <a href={resumeLink} target="_blank" rel="noopener noreferrer" className="px-4 py-2 rounded-lg text-xs font-semibold transition-all hover:shadow-md border-2 flex items-center gap-1.5" style={{ borderColor: primaryColor, color: primaryColor, backgroundColor: 'transparent' }}>
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6M9 17h6M13 3v6h6"/></svg>
                    View Resume
                  </a>
                ) : (
                  <button type="button" onClick={() => onViewResume && onViewResume(cvc.doc_id, cvc)} className="px-4 py-2 rounded-lg text-xs font-semibold transition-all hover:shadow-md border-2 flex items-center gap-1.5" style={{ borderColor: primaryColor, color: primaryColor, backgroundColor: 'transparent' }}>
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6M9 17h6M13 3v6h6"/></svg>
                    View Resume
                  </button>
                )
              )}

              {/* action plan buttons — skip follow_up if already shown above */}
              {actionPlan.filter(a => !(a.key === 'follow_up' && hasEmailOutreach)).map((action, actionIndex) => {
                const candidateId = cvc.id || cvc.candidate_id;
                const isLoading = outreachLoading[candidateId];
                return (
                  <button
                    key={action.key}
                    onClick={() => {
                      if (action.key === 'send_outreach') {
                        if (onCandidateAction && cvc) {
                          onCandidateAction('send_outreach', cvc);
                          return;
                        }
                        if (onUserQuery) {
                          onUserQuery(getCandidateActionCommand('send_outreach', cvc), 'agent');
                        }
                        return;
                      }
                      if (action.key === 'follow_up') {
                        startInlineFollowup(candidateData, emailReplies);
                        return;
                      }
                      if (action.key === 'request_resume') {
                        if (onCandidateAction && cvc) {
                          onCandidateAction('request_resume', cvc);
                          return;
                        }
                        if (onUserQuery) {
                          onUserQuery(getCandidateActionCommand('request_resume', cvc), 'agent');
                        }
                        return;
                      }
                      if (action.key === 'schedule_meeting') {
                        handleSendMessage(getCandidateActionCommand('schedule_meeting', cvc), 'agent');
                        return;
                      }
                      if (onCandidateAction && cvc) {
                        onCandidateAction(action.key, cvc);
                        return;
                      }
                      if (onUserQuery) {
                        onUserQuery(getCandidateActionCommand(action.key, cvc), 'agent');
                      }
                    }}
                    disabled={isLoading}
                    className="px-4 py-2 rounded-lg text-xs font-semibold transition-all hover:shadow-md flex items-center gap-1.5"
                    style={{
                      background: actionIndex === 0 ? 'linear-gradient(135deg, ' + primaryColor + ', ' + (secondaryColor || primaryColor) + ')' : (isDark ? '#374151' : '#F3F4F6'),
                      color: actionIndex === 0 ? '#fff' : (isDark ? '#E5E7EB' : '#1F2937'),
                      border: actionIndex === 0 ? 'none' : `1px solid ${isDark ? '#4B5563' : '#D1D5DB'}`,
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      opacity: isLoading ? 0.6 : 1
                    }}
                  >
                    {isLoading ? (
                      <>
                        <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending...
                      </>
                    ) : (
                      action.label
                    )}
                  </button>
                );
              })}

              {/* Reject — placed LAST so it's out of the way of the primary actions (accidental
                  taps are annoying even with the confirm). Hidden once terminal; backend gates scheduled. */}
              {(() => {
                const fs = String(candidateData.funnel_stage || candidateData.status || '').toLowerCase();
                if (['rejected', 'declined', 'hired'].includes(fs)) return null;
                // Hide Reject while an interview is scheduled/pending — Shortlisted (booking advances here)
                // or a booked-but-not-completed meeting. It returns once the interview completes (→ the
                // stage advances to Interviewed). Don't offer Reject mid-interview-process.
                if (fs === 'shortlisted' || (scheduledInterview && !hasCompletedInterview)) return null;
                return (
                  <button
                    type="button"
                    onClick={() => setRejectCandidate(candidateData)}
                    className="ml-auto px-4 py-2 rounded-lg text-xs font-semibold transition-all hover:shadow-md border-2 flex items-center gap-1.5 border-rose-300 text-rose-600 hover:bg-rose-50 dark:border-rose-900/60 dark:text-rose-400 dark:hover:bg-rose-900/20"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    Reject
                  </button>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
    );
  };


  return (
    <div className="flex-1 flex flex-col bg-white dark:bg-gray-900 min-h-0 overflow-hidden">
      {showHeader ? (
        <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4">
          <h1 className="chat_heading_md chat_font_semibold text-gray-900 dark:text-white chat_mb_xs">
            {jobTitle || 'AgentFlow Job Chat'}
          </h1>
          <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
            <span>Last updated {lastUpdated || 'July 23, 2023, 10:30 AM'}</span>
            <span>•</span>
            <span>Duration: {duration || '1 hour'}</span>
          </div>
        </div>
      ) : null}

      {/* Agent stage banner — OUTSIDE scroll container so it's always visible */}
      <AgentStageBanner
        stageLabel={stageLabel}
        cancellable={stageCancellable}
        onCancel={onStageCancel}
        primaryColor={primaryColor}
      />

      {/* Chat Messages Container (relative wrapper for scroll-to-bottom button) */}
      <div className="flex-1 relative min-h-0">
        <div
          ref={messagesContainerRef}
          className="chat-scrollbar-transparent-track absolute inset-0 overflow-y-auto px-6 py-6"
          style={{ overscrollBehavior: 'contain' }}
          onScroll={(e) => {
            const el = e.currentTarget;
            const atBottom = el.scrollHeight - el.scrollTop <= el.clientHeight + 150;
            // Debounce: only update state after user stops scrolling for 150ms
            if (scrollDebounceRef.current) clearTimeout(scrollDebounceRef.current);
            scrollDebounceRef.current = setTimeout(() => {
              setUserScrolledUp(!atBottom);
            }, 150);
          }}
        >
        {/* Show loader when fetching history and no messages yet */}
        {historyLoading && messages.length === 0 && (
          <div className="flex justify-center chat_mb_md">
            <div
              className="chat_rounded_lg px-6 py-3 chat_gradient_bg"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <span
                style={{
                  color: isDark ? '#E5E7EB' : '#1F2937',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                Loading conversation
              </span>
              <span style={{ display: 'flex', gap: '2px' }}>
                {[1, 2, 3].map((index) => (
                  <span
                    key={index}
                    className="job-posting-dot"
                    style={{
                      color: isDark ? '#9CA3AF' : '#374151',
                      fontSize: '1.2rem',
                      lineHeight: '1',
                    }}
                  >
                    •
                  </span>
                ))}
              </span>
            </div>
          </div>
        )}

        {messages.map((msg, index) => {
          const loaderText = 'Sourcing Strategy & JD creation in progress...';
          const searchingText = 'Searching across LinkedIn, GitHub, and other sources...';
          const isLoader =
            msg.sender === 'agent' && msg.message && msg.message.trim() === loaderText;
          const isSearching =
            msg.sender === 'agent' && msg.message && msg.message.trim() === searchingText;
          const hasCompleted =
            messages.some((m) =>
              typeof m.message === 'string' &&
              m.message.toLowerCase().startsWith('jd & sourcing strategy are ready')
            );

          // Sync status messages — render as centered pills (like job posting progress)
          const msgTrimmed = (msg.sender === 'agent' && typeof msg.message === 'string') ? msg.message.trim() : '';
          const isSyncInProgress = msgTrimmed === '🔄 Syncing replies in progress… Please wait.';
          const isSyncComplete = /^✅ Sync complete\s*—/.test(msgTrimmed);
          const isSyncStarted = msgTrimmed === 'Sync started. You can track progress in the sync status panel.';
          // Hide old "Checking Gmail" messages that may still exist in DB from previous syncs
          const isObsoleteGmailCheck = msgTrimmed === '🔍 Checking Gmail connection…';

          // Agent transition pills — centered pill between bubbles
          // Detect both live transitions (__AGENT_TRANSITION__ prefix) and DB-loaded ones (metadata.type)
          const isAgentTransition = msg.sender === 'agent' && typeof msg.message === 'string' && (
            msg.message.startsWith('__AGENT_TRANSITION__') ||
            msg.metadata?.type === 'agent_transition'
          );

          // Step pills for multi-step flows (scheduling phases)
          const isStepPill = msg.sender === 'agent' && typeof msg.message === 'string' &&
            msg.message.startsWith('__STEP_PILL__');

          // Check if we need to show a date separator before this message
          const prevMsg = index > 0 ? messages[index - 1] : null;
          const currentDateStr = getMessageDateString(msg.timestamp);
          const prevDateStr = prevMsg ? getMessageDateString(prevMsg.timestamp) : null;
          const showDateSeparator = currentDateStr && currentDateStr !== prevDateStr;
          const dateSeparatorText = showDateSeparator ? formatDateSeparator(msg.timestamp) : null;

          // Render agent transition as a centered pill
          if (isAgentTransition) {
            const transitionText = msg.message.startsWith('__AGENT_TRANSITION__')
              ? msg.message.replace('__AGENT_TRANSITION__', '')
              : msg.message;
            const isExit = transitionText.startsWith('Exited') || msg.metadata?.action === 'exited';

            const pillBg = isExit
              ? (isDark ? '#374151' : '#f3f4f6')
              : (isDark ? `${primaryColor}25` : `${primaryColor}15`);
            const pillBorder = isExit
              ? (isDark ? '#4b5563' : '#d1d5db')
              : (isDark ? `${primaryColor}50` : `${primaryColor}40`);
            const pillColor = isExit
              ? (isDark ? '#9ca3af' : '#6b7280')
              : (isDark ? '#e5e7eb' : primaryColor);
            const dotColor = isExit
              ? (isDark ? '#6b7280' : '#9ca3af')
              : primaryColor;

            return (
              <div key={msg.id} className="flex justify-center my-3">
                <div
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold shadow-sm"
                  style={{
                    backgroundColor: pillBg,
                    border: `1.5px solid ${pillBorder}`,
                    color: pillColor,
                  }}
                >
                  <span
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: dotColor }}
                  />
                  {transitionText}
                </div>
              </div>
            );
          }

          // Render step pill as a subtle centered indicator (no dot, muted style)
          if (isStepPill) {
            const stepText = msg.message.replace('__STEP_PILL__', '');
            return (
              <div key={msg.id} className="flex justify-center my-2">
                <div
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium"
                  style={{
                    backgroundColor: isDark ? '#1f2937' : '#f9fafb',
                    border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
                    color: isDark ? '#9ca3af' : '#6b7280',
                  }}
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                  {stepText}
                </div>
              </div>
            );
          }

          if (isLoader) {
            return (
              <React.Fragment key={msg.id}>
                {showDateSeparator && dateSeparatorText && (
                  <div className="flex justify-center items-center chat_my_md">
                    <span
                      className="chat_text_sm"
                      style={{
                        color: isDark ? '#9CA3AF' : '#6B7280',
                        fontSize: '12px',
                        padding: '4px 12px',
                      }}
                    >
                      {dateSeparatorText}
                    </span>
                  </div>
                )}
                <div className="flex justify-center chat_mb_md">
                  <div
                    className="chat_loader_pill"
                    style={{
                      opacity: hasCompleted ? 0.2 : 1,
                    }}
                  >
                    <span className="chat_text_md chat_font_normal">
                      {loaderText}
                    </span>
                    <span className="inline-block ml-1 chat_text_md">
                      <span className="animate-pulse">.</span>
                      <span className="animate-pulse" style={{ animationDelay: '0.2s' }}>
                        .
                      </span>
                      <span className="animate-pulse" style={{ animationDelay: '0.4s' }}>
                        .
                      </span>
                    </span>
                  </div>
                </div>
              </React.Fragment>
            );
          }

          if (isSearching) {
            return (
              <React.Fragment key={msg.id}>
                {showDateSeparator && dateSeparatorText && (
                  <div className="flex justify-center items-center chat_my_md">
                    <span
                      className="chat_text_sm"
                      style={{
                        color: isDark ? '#9CA3AF' : '#6B7280',
                        fontSize: '12px',
                        padding: '4px 12px',
                      }}
                    >
                      {dateSeparatorText}
                    </span>
                  </div>
                )}
                <div className="flex justify-center chat_mb_md">
                  <div
                    className="chat_loader_pill"
                    style={{
                      opacity: sourcingComplete ? 0.2 : 1,
                    }}
                  >
                    <span className="chat_text_md chat_font_normal">
                      {searchingText}
                    </span>
                  </div>
                </div>
              </React.Fragment>
            );
          }

          // Render candidate card from metadata
          if (msg.metadata?.type === 'candidate_card' && msg.metadata?.candidate) {
            return (
              <React.Fragment key={msg.id}>
                {showDateSeparator && dateSeparatorText && (
                  <div className="flex justify-center items-center chat_my_md">
                    <span
                      className="chat_text_sm"
                      style={{
                        color: isDark ? '#9CA3AF' : '#6B7280',
                        fontSize: '12px',
                        padding: '4px 12px',
                      }}
                    >
                      {dateSeparatorText}
                    </span>
                  </div>
                )}
                {renderInlineCandidateCard(msg.metadata.candidate, `candidate-card-${msg.id}`)}
              </React.Fragment>
            );
          }

          if (msg.metadata?.type === 'interview_creation' && typeof renderInterviewCreation === 'function') {
            return (
              <React.Fragment key={msg.id}>
                {showDateSeparator && dateSeparatorText && (
                  <div className="flex justify-center items-center chat_my_md">
                    <span
                      className="chat_text_sm"
                      style={{
                        color: isDark ? '#9CA3AF' : '#6B7280',
                        fontSize: '12px',
                        padding: '4px 12px',
                      }}
                    >
                      {dateSeparatorText}
                    </span>
                  </div>
                )}
                <div className="flex justify-start mb-4" style={{ paddingLeft: '16px', paddingRight: '16px' }}>
                  <div className="w-full max-w-5xl">
                    {renderInterviewCreation(msg.metadata.payload || {}, {
                      messageId: msg.id,
                      externalMessage: interviewCreationInputs[msg.id] || null,
                      onExternalMessageProcessed: (token) => {
                        setInterviewCreationInputs((prev) => {
                          if (prev[msg.id]?.token !== token) return prev;
                          const next = { ...prev };
                          delete next[msg.id];
                          return next;
                        });
                      },
                      dismiss: () => setMessages((prev) => prev.filter((item) => item.id !== msg.id)),
                    })}
                  </div>
                </div>
              </React.Fragment>
            );
          }

          // Render inline follow-up chat messages (warning step before routing to agent)
          if (msg.metadata?.type === 'inline_followup') {
            const fu = msg.metadata;
            return (
              <React.Fragment key={msg.id}>
                {showDateSeparator && dateSeparatorText && (
                  <div className="flex justify-center items-center chat_my_md">
                    <span className="chat_text_sm" style={{ color: isDark ? '#9CA3AF' : '#6B7280', fontSize: '12px', padding: '4px 12px' }}>{dateSeparatorText}</span>
                  </div>
                )}
                <div className="flex justify-start mb-3" style={{ paddingLeft: '16px', paddingRight: '16px' }}>
                  <div style={{ maxWidth: '480px' }}>
                    {fu.step === 'warning' && (
                      <div className="rounded-xl p-4 space-y-3" style={{ background: isDark ? '#1C1407' : '#FFFBEB', border: `1px solid ${isDark ? '#92400E55' : '#FCD34D'}` }}>
                        <div className="flex items-start gap-2">
                          <svg className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#F59E0B' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"/>
                          </svg>
                          <div>
                            <p className="text-sm font-semibold" style={{ color: isDark ? '#FCD34D' : '#92400E' }}>Recent Follow-up Detected</p>
                            <p className="text-xs mt-0.5" style={{ color: isDark ? '#D1D5DB' : '#78350F' }}>
                              Last email to <strong>{fu.candidate?.name}</strong> sent {fu.lastSentAt
                                ? (() => { const hrs = Math.floor((Date.now() - new Date(fu.lastSentAt).getTime()) / 3600000); return hrs < 1 ? 'less than 1 hour ago' : `${hrs} hour${hrs === 1 ? '' : 's'} ago`; })()
                                : 'recently'} — sending too frequently may reduce engagement.
                            </p>
                          </div>
                        </div>
                        <p className="text-xs italic" style={{ color: isDark ? '#9CA3AF' : '#78350F' }}>↓ Type your response in the chat below, or use the buttons.</p>
                        <div className="flex gap-2">
                          <button
                            onClick={() => dismissFollowupMsg(msg.id)}
                            className="px-3 py-1.5 text-xs rounded-lg border"
                            style={{ borderColor: isDark ? '#4B5563' : '#D1D5DB', color: isDark ? '#9CA3AF' : '#6B7280', background: 'transparent' }}
                          >Cancel</button>
                          <button
                            onClick={() => {
                              dismissFollowupMsg(msg.id);
                              handleSendMessage(getCandidateActionCommand('follow_up', fu.candidate), 'agent');
                            }}
                            className="px-3 py-1.5 text-xs rounded-lg font-semibold text-white"
                            style={{ background: '#F59E0B' }}
                          >Send Anyway</button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </React.Fragment>
            );
          }

          if (msg.metadata?.type === 'jd_card' && msg.metadata?.jdText) {
            return (
              <React.Fragment key={msg.id}>
                {showDateSeparator && dateSeparatorText && (
                  <div className="flex justify-center items-center chat_my_md">
                    <span
                      className="chat_text_sm"
                      style={{
                        color: isDark ? '#9CA3AF' : '#6B7280',
                        fontSize: '12px',
                        padding: '4px 12px',
                      }}
                    >
                      {dateSeparatorText}
                    </span>
                  </div>
                )}
                <div className="flex justify-start mb-4" style={{ paddingLeft: '16px', paddingRight: '16px' }}>
                  <div style={{ width: '100%', maxWidth: '100%' }}>
                    <JDOverview
                      jdText={msg.metadata.jdText}
                      requirements={msg.metadata.requirements || {}}
                      showSourcingStrategy={false}
                      showActionButtons={false}
                    />
                  </div>
                </div>
              </React.Fragment>
            );
          }

          // Hide obsolete "Checking Gmail connection" messages from DB
          if (isObsoleteGmailCheck || (isSyncStarted && hasActiveSyncState)) {
            return null;
          }

          // Sync "in progress" — centered pill with animation
          if (isSyncInProgress) {
            return (
              <React.Fragment key={msg.id}>
                {showDateSeparator && dateSeparatorText && (
                  <div className="flex justify-center items-center chat_my_md">
                    <span
                      className="chat_text_sm"
                      style={{
                        color: isDark ? '#9CA3AF' : '#6B7280',
                        fontSize: '12px',
                        padding: '4px 12px',
                      }}
                    >
                      {dateSeparatorText}
                    </span>
                  </div>
                )}
                <div className="flex justify-center chat_mb_md">
                  <div
                    className="chat_rounded_lg px-5 py-3"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      background: isDark
                        ? 'linear-gradient(135deg, #1E3A5F 0%, #1A2B40 100%)'
                        : 'linear-gradient(135deg, #EBF5FF 0%, #E0F2FE 100%)',
                      border: `1px solid ${isDark ? '#2563EB44' : '#93C5FD88'}`,
                      opacity: isSyncing ? 1 : 0.5,
                    }}
                  >
                    {isSyncing && (
                      <svg
                        className="animate-spin"
                        style={{ width: 16, height: 16, flexShrink: 0, color: primaryColor }}
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                    )}
                    <span
                      style={{
                        fontSize: '13px',
                        fontWeight: 600,
                        color: isDark ? '#93C5FD' : '#1D4ED8',
                      }}
                    >
                      🔄 Syncing replies in progress… Please wait.
                    </span>
                  </div>
                </div>
              </React.Fragment>
            );
          }

          // Sync "complete" — centered pill (static)
          if (isSyncComplete) {
            // Split: first line is the centered header, rest (if any) is the report body
            const syncLines = msgTrimmed.split('\n');
            const syncHeader = syncLines[0].trim();
            const syncBody = syncLines.slice(1).join('\n').trim();
            return (
              <React.Fragment key={msg.id}>
                {showDateSeparator && dateSeparatorText && (
                  <div className="flex justify-center items-center chat_my_md">
                    <span
                      className="chat_text_sm"
                      style={{
                        color: isDark ? '#9CA3AF' : '#6B7280',
                        fontSize: '12px',
                        padding: '4px 12px',
                      }}
                    >
                      {dateSeparatorText}
                    </span>
                  </div>
                )}
                <div className="flex justify-center chat_mb_md">
                  <div
                    className="chat_rounded_lg px-5 py-3"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      background: isDark
                        ? 'linear-gradient(135deg, #0D3320 0%, #1A2B20 100%)'
                        : 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)',
                      border: `1px solid ${isDark ? '#10B98144' : '#6EE7B788'}`,
                    }}
                  >
                    <span
                      style={{
                        fontSize: '13px',
                        fontWeight: 600,
                        color: isDark ? '#6EE7B7' : '#047857',
                      }}
                    >
                      {syncHeader}
                    </span>
                  </div>
                </div>
                {/* Render the report body (if present) as a regular agent message below the centered pill */}
                {syncBody && (
                  <ChatMessage
                    sender="agent"
                    message={syncBody}
                    timestamp={msg.timestamp}
                  />
                )}
              </React.Fragment>
            );
          }

          // Check if this is the channel selection message
          const isChannelSelectionMessage = 
            msg.sender === 'agent' && 
            msg.message && 
            msg.message.toLowerCase().includes('select the channel');
          
          // Check if this message has channel selection metadata (for restoring from database)
          const hasChannelSelectionMetadata = 
            msg.metadata?.phase === 'job_posting_channel_selection' ||
            (typeof msg.metadata === 'string' && msg.metadata.includes('job_posting_channel_selection'));
          
          // Parse metadata if it's a string
          let parsedMetadata = msg.metadata;
          if (typeof msg.metadata === 'string') {
            try {
              parsedMetadata = JSON.parse(msg.metadata);
            } catch (e) {
              parsedMetadata = {};
            }
          }
          
          // Get available channels from metadata (for restoring buttons from database)
          const metadataChannels = parsedMetadata?.available_channels || [];
          const metadataPostedChannels = parsedMetadata?.already_posted || [];
          
          // Determine if we should show channel buttons
          // Show buttons if: (phase is channel_selection AND it's a channel selection message) OR (message has channel selection metadata)
          const shouldShowChannelButtons = 
            (isChannelSelectionMessage && jobPostingPhase === 'channel_selection') ||
            (hasChannelSelectionMetadata && metadataChannels.length > 0);
          
          // Use metadata channels if available, otherwise use props
          const buttonsChannelOptions = metadataChannels.length > 0 
            ? metadataChannels.filter((c) => !metadataPostedChannels.map(p => p.toLowerCase()).includes(c.toLowerCase()))
            : effectiveChannelOptions;

          return (
            <React.Fragment key={msg.id}>
              {showDateSeparator && dateSeparatorText && (
                <div className="flex justify-center items-center chat_my_md">
                  <span
                    className="chat_text_sm"
                    style={{
                      color: isDark ? '#9CA3AF' : '#6B7280',
                      fontSize: '12px',
                      padding: '4px 12px',
                    }}
                  >
                    {dateSeparatorText}
                  </span>
                </div>
              )}
              {draftTemplateEditor.open && draftTemplateEditor.messageId === msg.id ? (
                <div
                  className="chat_mb_md"
                  style={{
                    padding: '12px',
                    borderRadius: '14px',
                    border: `1px solid ${isDark ? '#374151' : '#C7D2FE'}`,
                    background: isDark ? '#111827' : '#EEF2FF',
                  }}
                >
                  <textarea
                    value={draftTemplateEditor.value}
                    onChange={(event) => setDraftTemplateEditor((prev) => ({
                      ...prev,
                      value: event.target.value,
                      error: '',
                    }))}
                    rows={14}
                    style={{
                      width: '100%',
                      minHeight: '260px',
                      resize: 'vertical',
                      border: `1px solid ${isDark ? '#4B5563' : '#CBD5E1'}`,
                      borderRadius: '10px',
                      padding: '12px',
                      fontSize: '13px',
                      lineHeight: '20px',
                      color: isDark ? '#F9FAFB' : '#111827',
                      background: isDark ? '#0B1220' : '#FFFFFF',
                      outline: 'none',
                      whiteSpace: 'pre-wrap',
                    }}
                  />
                  {draftTemplateEditor.error && (
                    <div
                      style={{
                        marginTop: '6px',
                        color: isDark ? '#FCA5A5' : '#B91C1C',
                        fontSize: '12px',
                      }}
                    >
                      {draftTemplateEditor.error}
                    </div>
                  )}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      gap: '8px',
                      marginTop: '10px',
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setDraftTemplateEditor({ open: false, messageId: null, value: '', error: '' })}
                      className="chat_font_medium chat_transition"
                      style={{
                        padding: '6px 14px',
                        borderRadius: '999px',
                        border: `1px solid ${isDark ? '#374151' : '#D1D5DB'}`,
                        background: isDark ? '#1F2937' : '#FFFFFF',
                        color: isDark ? '#F3F4F6' : '#111827',
                        fontSize: '12px',
                        cursor: 'pointer',
                      }}
                    >
                      Cancel Edit
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveDraftTemplateEdit}
                      className="chat_font_medium chat_transition"
                      style={{
                        padding: '6px 14px',
                        borderRadius: '999px',
                        border: '1px solid #10B981',
                        background: isDark ? '#064E3B' : '#D1FAE5',
                        color: isDark ? '#6EE7B7' : '#065F46',
                        fontSize: '12px',
                        cursor: 'pointer',
                      }}
                    >
                      Save Template
                    </button>
                  </div>
                </div>
              ) : (
                <ChatMessage
                  message={msg.message}
                  sender={msg.sender}
                  timestamp={msg.timestamp}
                  mode={msg.mode}
                />
              )}
              {shouldShowChannelButtons && (
                <div className="chat_mb_md" style={{ marginLeft: '0' }}>
                  {((metadataPostedChannels.length > 0) || (Array.isArray(postedChannels) && postedChannels.length > 0)) ? (
                    <div
                      className="chat_text_xs chat_mb_sm"
                      style={{ color: isDark ? '#9CA3AF' : '#6B7280' }}
                    >
                      Already posted in: {(metadataPostedChannels.length > 0 ? metadataPostedChannels : postedChannels).join(', ')}
                    </div>
                  ) : null}
                  {Array.isArray(buttonsChannelOptions) && buttonsChannelOptions.length === 0 ? (
                    <div
                      className="chat_text_xs chat_mb_sm"
                      style={{ color: isDark ? '#9CA3AF' : '#6B7280' }}
                    >
                      No remaining channels available for posting.
                    </div>
                  ) : null}
                  <div className="flex flex-wrap gap-2">
                    {(Array.isArray(buttonsChannelOptions) ? buttonsChannelOptions : []).map((channel) => (
                    <button
                      key={channel}
                      onClick={() => {
                        if (!onChannelSelect) return;

                        // Multi-select: toggle this channel in the list
                        let newChannels;
                        if (selectedChannels.includes(channel)) {
                          newChannels = selectedChannels.filter((c) => c !== channel);
                        } else {
                          newChannels = [...selectedChannels, channel];
                        }

                        onChannelSelect(newChannels);

                        // Auto-fill input with "Post JD to LinkedIn and Naukri"
                        if (newChannels.length > 0) {
                          const formatChannelList = (list) => {
                            if (list.length === 1) return list[0];
                            if (list.length === 2) return `${list[0]} and ${list[1]}`;
                            const allButLast = list.slice(0, -1).join(', ');
                            const last = list[list.length - 1];
                            return `${allButLast} and ${last}`;
                          };

                          const channelText = `Post JD to ${formatChannelList(newChannels)}`;
                          setInputMessage(channelText);

                          // Focus the input after a short delay
                          setTimeout(() => {
                            if (inputRef.current) {
                              inputRef.current.focus();
                            }
                          }, 100);
                        } else {
                          setInputMessage('');
                        }
                      }}
                      className="chat_font_medium chat_transition"
                      style={{
                        paddingTop: '8px',
                        paddingRight: '16px',
                        paddingBottom: '8px',
                        paddingLeft: '16px',
                        borderRadius: '16px',
                        border: `1px solid ${
                          selectedChannels.includes(channel)
                            ? primaryColor
                            : isDark
                            ? '#374151'
                            : '#D1D5DB'
                        }`,
                        backgroundColor: selectedChannels.includes(channel)
                          ? primaryColor
                          : isDark
                          ? '#1F2937'
                          : '#ffffff',
                        color: selectedChannels.includes(channel)
                          ? '#ffffff'
                          : isDark
                          ? '#F9FAFB'
                          : '#212529',
                        fontSize: '14px',
                        lineHeight: '20px',
                      }}
                      >
                        {channel}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })}
        {workspaceSelectionRequired && (
          <div
            className="chat_mb_md"
            style={{
              width: 'min(100%, 520px)',
              marginLeft: '4px',
              padding: '14px',
              border: `1px solid ${isDark ? '#374151' : '#C7D2FE'}`,
              borderRadius: '8px',
              background: isDark ? '#111827' : '#FFFFFF',
              boxShadow: isDark
                ? '0 10px 24px rgba(0, 0, 0, 0.18)'
                : '0 10px 24px rgba(15, 23, 42, 0.08)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '12px' }}>
              <span
                style={{
                  width: '30px',
                  height: '30px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  borderRadius: '6px',
                  background: isDark ? '#312E81' : '#EEF2FF',
                  color: isDark ? '#C7D2FE' : primaryColor,
                }}
              >
                <FiBriefcase size={15} />
              </span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: isDark ? '#F9FAFB' : '#111827' }}>
                  Choose workspace
                </div>
                <div style={{ marginTop: '2px', fontSize: '11px', color: isDark ? '#9CA3AF' : '#64748B' }}>
                  Required before confirming this JD
                </div>
              </div>
            </div>

            {!creatingWorkspace && Array.isArray(workspaceOptions) && workspaceOptions.length > 0 && (
              <div style={{ position: 'relative' }}>
                <button
                  type="button"
                  onClick={() => setWorkspaceDropdownOpen((open) => !open)}
                  aria-expanded={workspaceDropdownOpen}
                  aria-haspopup="listbox"
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '10px',
                    border: `1px solid ${workspaceDropdownOpen ? primaryColor : isDark ? '#4B5563' : '#CBD5E1'}`,
                    borderRadius: '6px',
                    padding: '9px 10px',
                    fontSize: '12px',
                    color: isDark ? '#F9FAFB' : '#111827',
                    background: isDark ? '#0B1220' : '#FFFFFF',
                    cursor: 'pointer',
                  }}
                >
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {selectedWorkspaceName || 'Select a workspace'}
                  </span>
                  <FiChevronDown size={15} style={{ flexShrink: 0 }} />
                </button>

                {workspaceDropdownOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      zIndex: 30,
                      top: 'calc(100% + 5px)',
                      left: 0,
                      right: 0,
                      padding: '7px',
                      border: `1px solid ${isDark ? '#374151' : '#CBD5E1'}`,
                      borderRadius: '7px',
                      background: isDark ? '#111827' : '#FFFFFF',
                      boxShadow: '0 12px 28px rgba(15, 23, 42, 0.16)',
                    }}
                  >
                    <div style={{ position: 'relative', marginBottom: '6px' }}>
                      <FiSearch
                        size={13}
                        style={{
                          position: 'absolute',
                          left: '9px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: isDark ? '#9CA3AF' : '#64748B',
                        }}
                      />
                      <input
                        type="search"
                        autoFocus
                        value={workspaceFilter}
                        onChange={(event) => setWorkspaceFilter(event.target.value)}
                        placeholder="Search workspaces"
                        style={{
                          width: '100%',
                          border: `1px solid ${isDark ? '#4B5563' : '#CBD5E1'}`,
                          borderRadius: '6px',
                          padding: '7px 9px 7px 28px',
                          fontSize: '12px',
                          color: isDark ? '#F9FAFB' : '#111827',
                          background: isDark ? '#0B1220' : '#FFFFFF',
                          outline: 'none',
                        }}
                      />
                    </div>

                    <div role="listbox" style={{ maxHeight: '190px', overflowY: 'auto' }}>
                      {filteredWorkspaceOptions.map((workspace) => (
                        <button
                          key={workspace.wid}
                          type="button"
                          role="option"
                          aria-selected={selectedWorkspaceName === workspace.name}
                          onClick={() => {
                            setSelectedWorkspaceName(workspace.name);
                            setWorkspaceDropdownOpen(false);
                            setWorkspaceFilter('');
                          }}
                          style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '8px',
                            padding: '7px 8px',
                            border: 'none',
                            borderRadius: '5px',
                            background: selectedWorkspaceName === workspace.name
                              ? (isDark ? '#312E81' : '#EEF2FF')
                              : 'transparent',
                            color: isDark ? '#F9FAFB' : '#1F2937',
                            fontSize: '12px',
                            textAlign: 'left',
                            cursor: 'pointer',
                          }}
                        >
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {workspace.name}
                          </span>
                          {selectedWorkspaceName === workspace.name && <FiCheck size={13} />}
                        </button>
                      ))}
                      {filteredWorkspaceOptions.length === 0 && (
                        <div style={{ padding: '10px 8px', fontSize: '11px', color: isDark ? '#9CA3AF' : '#64748B' }}>
                          No matching workspaces
                        </div>
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        setCreatingWorkspace(true);
                        setWorkspaceDropdownOpen(false);
                        setNewWorkspaceName(workspaceFilter.trim());
                        setWorkspaceFilter('');
                      }}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '7px',
                        marginTop: '6px',
                        padding: '8px',
                        border: 'none',
                        borderTop: `1px solid ${isDark ? '#374151' : '#E2E8F0'}`,
                        background: 'transparent',
                        color: isDark ? '#C7D2FE' : primaryColor,
                        fontSize: '12px',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      <FiPlus size={14} />
                      Create new workspace
                    </button>
                  </div>
                )}
              </div>
            )}

            {creatingWorkspace && (
              <input
                type="text"
                autoFocus
                value={newWorkspaceName}
                onChange={(event) => setNewWorkspaceName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && newWorkspaceName.trim()) {
                    event.preventDefault();
                    handleSendMessage(newWorkspaceName.trim(), 'agent');
                  }
                }}
                placeholder="Enter new workspace name"
                maxLength={255}
                style={{
                  width: '100%',
                  border: `1px solid ${isDark ? '#4B5563' : '#CBD5E1'}`,
                  borderRadius: '6px',
                  padding: '9px 10px',
                  fontSize: '12px',
                  color: isDark ? '#F9FAFB' : '#111827',
                  background: isDark ? '#0B1220' : '#FFFFFF',
                  outline: 'none',
                }}
              />
            )}

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '8px',
                marginTop: '11px',
              }}
            >
              {Array.isArray(workspaceOptions) && workspaceOptions.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    setCreatingWorkspace((creating) => !creating);
                    setNewWorkspaceName('');
                    setWorkspaceDropdownOpen(false);
                  }}
                  style={{
                    border: 'none',
                    padding: '6px 0',
                    background: 'transparent',
                    color: isDark ? '#C7D2FE' : primaryColor,
                    fontSize: '11px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  {creatingWorkspace ? 'Choose existing' : 'Add new workspace'}
                </button>
              )}
              <button
                type="button"
                disabled={creatingWorkspace ? !newWorkspaceName.trim() : !selectedWorkspaceName}
                onClick={() => handleSendMessage(
                  creatingWorkspace ? newWorkspaceName.trim() : selectedWorkspaceName,
                  'agent',
                )}
                style={{
                  marginLeft: 'auto',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '7px 12px',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#FFFFFF',
                  background: primaryColor,
                  cursor: (creatingWorkspace ? newWorkspaceName.trim() : selectedWorkspaceName)
                    ? 'pointer'
                    : 'not-allowed',
                  opacity: (creatingWorkspace ? newWorkspaceName.trim() : selectedWorkspaceName) ? 1 : 0.5,
                  whiteSpace: 'nowrap',
                }}
              >
                {creatingWorkspace ? 'Create and continue' : 'Use workspace'}
              </button>
            </div>
          </div>
        )}
        {/* Draft Edit Action Buttons (Edit Message, Edit Signature, Send, Cancel) */}
        {Array.isArray(draftEditActions) && draftEditActions.length > 0 && (
          <div className="flex flex-wrap gap-2 chat_mb_md" style={{ paddingLeft: '4px' }}>
            {draftEditActions.map((action, idx) => {
              const cmd = action.command || action.action || '';
              const isConfirm = /^(yes|send|confirm|proceed)$/i.test(cmd);
              const isDeny = /^(cancel|no|discard|stop)$/i.test(cmd);
              const isEdit = /^(edit_template|edit|edit_message)$/i.test(cmd);
              return (
                <button
                  key={`draft_action_${idx}`}
                  type="button"
                  onClick={() => {
                    if (isEdit) {
                      openInlineTemplateEditor();
                      if (onDraftEditAction) onDraftEditAction(cmd);
                      return;
                    }
                    if (onDraftEditAction) onDraftEditAction(cmd);
                    handleSendMessage(cmd);
                  }}
                  className="chat_font_medium chat_transition"
                  style={{
                    padding: '6px 14px',
                    borderRadius: '999px',
                    border: `1px solid ${isConfirm ? '#10B981' : isDeny ? '#EF4444' : isEdit ? primaryColor : isDark ? '#374151' : '#D1D5DB'}`,
                    backgroundColor: isConfirm
                      ? (isDark ? '#064E3B' : '#D1FAE5')
                      : isDeny
                      ? (isDark ? '#7F1D1D' : '#FEE2E2')
                      : isEdit
                      ? (isDark ? '#241245' : '#F4F0FF')
                      : isDark ? '#1F2937' : '#ffffff',
                    color: isConfirm
                      ? (isDark ? '#6EE7B7' : '#065F46')
                      : isDeny
                      ? (isDark ? '#FCA5A5' : '#991B1B')
                      : isEdit
                      ? primaryColor
                      : isDark ? '#F3F4F6' : '#111827',
                    fontSize: '12px',
                    lineHeight: '16px',
                    cursor: 'pointer',
                  }}
                >
                  {action.label}
                </button>
              );
            })}
          </div>
        )}
        {/* Waiting for Backend Questions Indicator (company name, salary, etc.) */}
        {waitingForBackendQuestion && (
          <div className="flex justify-center chat_mb_md">
            <div
              className="chat_rounded_lg px-6 py-3 chat_gradient_bg"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <style>{`
                @keyframes waiting-dot {
                  0%, 100% { opacity: 0.3; }
                  50% { opacity: 1; }
                }
                .waiting-dot {
                  animation: waiting-dot 1.2s ease-in-out infinite;
                }
                .waiting-dot:nth-child(1) { animation-delay: 0s; }
                .waiting-dot:nth-child(2) { animation-delay: 0.2s; }
                .waiting-dot:nth-child(3) { animation-delay: 0.4s; }
              `}</style>
              <span style={{ 
                color: isDark ? '#E5E7EB' : '#1F2937', 
                fontSize: '14px', 
                fontWeight: '500' 
              }}>
                Processing
              </span>
              <span style={{ display: 'flex', gap: '2px' }}>
                {[1, 2, 3].map((index) => (
                  <span
                    key={index}
                    className="waiting-dot"
                    style={{ 
                      color: isDark ? '#9CA3AF' : '#374151',
                      fontSize: '1.2rem',
                      lineHeight: '1',
                    }}
                  >
                    •
                  </span>
                ))}
              </span>
            </div>
          </div>
        )}
        {/* Job Posting In Progress Indicator - Only show during active processing, not after completion */}
        {jobPostingProcessing && jobPostingPhase === 'posting' && !jobPostingComplete && !jobPostingFailed && (
          <div 
            className="flex justify-center chat_mb_md"
          >
            <div
              className="chat_rounded_lg px-6 py-3 chat_gradient_bg"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <style>{`
                @keyframes pulse-dot {
                  0%, 100% { opacity: 0.4; }
                  50% { opacity: 1; }
                }
                .job-posting-dot {
                  animation: pulse-dot 1.4s ease-in-out infinite;
                }
                .job-posting-dot:nth-child(1) { animation-delay: 0s; }
                .job-posting-dot:nth-child(2) { animation-delay: 0.2s; }
                .job-posting-dot:nth-child(3) { animation-delay: 0.4s; }
              `}</style>
              <span style={{ 
                color: isDark ? '#E5E7EB' : '#1F2937', 
                fontSize: '14px', 
                fontWeight: '500' 
              }}>
                Job posting in progress
              </span>
              <span style={{ display: 'flex', gap: '2px' }}>
                {[1, 2, 3].map((index) => (
                  <span
                    key={index}
                    className="job-posting-dot"
                    style={{ 
                      color: isDark ? '#9CA3AF' : '#374151',
                      fontSize: '1.2rem',
                      lineHeight: '1',
                    }}
                  >
                    •
                  </span>
                ))}
              </span>
            </div>
          </div>
        )}
        {/* Typing Indicator (for other loading states, not job posting) */}
        {isLoading && !jobPostingProcessing && (
          <div className="flex justify-start chat_mb_md">
            <div
              className="max-w-[80%] chat_rounded_lg px-4 py-3 chat_gradient_bg"
            >
              <div className="flex items-center gap-1">
                <style>{`
                  @keyframes wave {
                    0%, 60%, 100% {
                      transform: translateY(0) scale(1);
                      opacity: 0.7;
                    }
                    30% {
                      transform: translateY(-8px) scale(1.2);
                      opacity: 1;
                    }
                  }
                  .typing-dot {
                    animation: wave 1.4s ease-in-out infinite;
                  }
                  .typing-dot:nth-child(1) {
                    animation-delay: 0s;
                  }
                  .typing-dot:nth-child(2) {
                    animation-delay: 0.2s;
                  }
                  .typing-dot:nth-child(3) {
                    animation-delay: 0.4s;
                  }
                `}</style>
                {[1, 2, 3].map((index) => (
                  <span
                    key={index}
                    className="typing-dot"
                    style={{ 
                      color: secondaryColor,
                      fontSize: '1.5rem',
                      lineHeight: '1',
                      display: 'inline-block',
                    }}
                  >
                    .
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
        </div>

        {/* Floating scroll-to-bottom button */}
        {userScrolledUp && (
          <button
            onClick={forceScrollToBottom}
            aria-label="Scroll to bottom"
            className="absolute z-10 flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-105"
            style={{
              bottom: '16px',
              right: '20px',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
              border: 'none',
              cursor: 'pointer',
              color: '#fff',
              animation: 'fadeInUp 0.2s ease-out',
              transition: 'transform 0.15s ease, box-shadow 0.15s ease',
            }}
          >
            <style>{`
              @keyframes fadeInUp {
                from { opacity: 0; transform: translateY(8px); }
                to { opacity: 1; transform: translateY(0); }
              }
            `}</style>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 4V16M10 16L5 11M10 16L15 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}
      </div>

      {/* Input Area */}
      <div className="shrink-0 sticky bottom-0 z-10 border-t border-gray-200 dark:border-gray-700 px-6 py-4 bg-white dark:bg-gray-900">
        {/* Confirmation Action Buttons */}
        {waitingForConfirmation && (
          <div className="mb-4">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">What would you like to do with this JD?</p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => {
                  handleSendMessage('proceed');
                }}
                className="chat_font_medium chat_transition hover:opacity-80"
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
                  color: '#ffffff',
                  fontSize: '13px',
                  cursor: 'pointer',
                  fontWeight: '600',
                }}
              >
                ✓ Yes, Proceed
              </button>
              <button
                type="button"
                onClick={() => {
                  if (inputRef.current) {
                    inputRef.current.focus();
                  }
                }}
                className="chat_font_medium chat_transition hover:opacity-80"
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: `1px solid ${primaryColor}`,
                  backgroundColor: isDark ? '#1F2937' : '#ffffff',
                  color: primaryColor,
                  fontSize: '13px',
                  cursor: 'pointer',
                }}
              >
                ✏️ Make Changes
              </button>
            </div>
          </div>
        )}
        {/* Dropdown Options for Naukri Field Selection */}
        {Array.isArray(pendingNaukriFieldOptions) && pendingNaukriFieldOptions.length > 0 && (
          <div className="mb-4">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Select an option:</p>
            <div 
              className="flex flex-wrap gap-2"
              style={{
                maxHeight: '400px',
                overflowY: 'auto',
                padding: '8px',
                border: `1px solid ${isDark ? '#374151' : '#D1D5DB'}`,
                borderRadius: '8px',
                backgroundColor: isDark ? '#111827' : '#F9FAFB'
              }}
            >
              {pendingNaukriFieldOptions.map((option, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => onNaukriFieldOptionSelect && onNaukriFieldOptionSelect(option)}
                  className="chat_font_medium chat_transition hover:opacity-80"
                  style={{
                    padding: '8px 16px',
                    borderRadius: '8px',
                    border: `1px solid ${primaryColor}`,
                    backgroundColor: isDark ? '#1F2937' : '#ffffff',
                    color: primaryColor,
                    fontSize: '13px',
                    cursor: 'pointer',
                    flex: '0 0 auto',
                    height: 'fit-content'
                  }}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
        )}
        {/* Ask-mode filter status bar — always visible in Ask mode */}
        {chatMode === 'ask' && (
          <div className="mb-3" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {/* Filter status bar */}
            <div
              className="flex items-center gap-1 flex-wrap"
              style={{
                padding: '8px 12px',
                borderRadius: '8px',
                backgroundColor: Array.isArray(filterBreadcrumbs) && filterBreadcrumbs.length > 0
                  ? (isDark ? '#1E293B' : '#EFF6FF')
                  : (isDark ? '#1F2937' : '#F3F4F6'),
                border: `1px solid ${
                  Array.isArray(filterBreadcrumbs) && filterBreadcrumbs.length > 0
                    ? (isDark ? '#334155' : '#BFDBFE')
                    : (isDark ? '#374151' : '#E5E7EB')
                }`,
                fontSize: '12px',
                lineHeight: '18px',
                color: Array.isArray(filterBreadcrumbs) && filterBreadcrumbs.length > 0
                  ? (isDark ? '#93C5FD' : '#1D4ED8')
                  : (isDark ? '#9CA3AF' : '#6B7280'),
              }}
            >
              {Array.isArray(filterBreadcrumbs) && filterBreadcrumbs.length > 0 ? (
                <>
                  <span style={{ marginRight: '4px', opacity: 0.7, fontWeight: 600 }}>Filters:</span>
                  {filterBreadcrumbs.map((crumb, i) => (
                    <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: '4px',
                        backgroundColor: isDark ? '#1E40AF' : '#DBEAFE',
                        color: isDark ? '#BFDBFE' : '#1E40AF',
                        fontWeight: 500,
                      }}>{crumb}</span>
                      {i < filterBreadcrumbs.length - 1 && (
                        <span style={{ opacity: 0.5 }}>→</span>
                      )}
                    </span>
                  ))}
                  {onClearFilters && filterBreadcrumbs.length === 1 && (
                    <button
                      type="button"
                      onClick={onClearFilters}
                      style={{
                        marginLeft: '8px',
                        padding: '2px 10px',
                        borderRadius: '4px',
                        border: `1px solid ${isDark ? '#4B5563' : '#D1D5DB'}`,
                        backgroundColor: isDark ? '#374151' : '#F9FAFB',
                        color: isDark ? '#D1D5DB' : '#374151',
                        fontSize: '11px',
                        cursor: 'pointer',
                        fontWeight: 500,
                      }}
                    >
                      Clear
                    </button>
                  )}
                  {onClearFilters && filterBreadcrumbs.length > 1 && (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          // Undo last filter — send "undo last filter" to backend
                          if (typeof onUserQuery === 'function') {
                            onUserQuery('undo last filter', 'ask');
                          }
                        }}
                        style={{
                          marginLeft: '8px',
                          padding: '2px 10px',
                          borderRadius: '4px',
                          border: `1px solid ${isDark ? '#FCD34D' : '#F59E0B'}`,
                          backgroundColor: isDark ? '#78350F' : '#FFFBEB',
                          color: isDark ? '#FCD34D' : '#92400E',
                          fontSize: '11px',
                          cursor: 'pointer',
                          fontWeight: 500,
                        }}
                      >
                        Undo Last
                      </button>
                      <button
                        type="button"
                        onClick={onClearFilters}
                        style={{
                          marginLeft: '4px',
                          padding: '2px 10px',
                          borderRadius: '4px',
                          border: `1px solid ${isDark ? '#4B5563' : '#D1D5DB'}`,
                          backgroundColor: isDark ? '#374151' : '#F9FAFB',
                          color: isDark ? '#D1D5DB' : '#374151',
                          fontSize: '11px',
                          cursor: 'pointer',
                          fontWeight: 500,
                        }}
                      >
                        Clear All
                      </button>
                    </>
                  )}
                </>
              ) : (
                <span style={{ fontStyle: 'italic' }}>
                  Pipeline mode — Search and filter candidates. Try "show all candidates" or "who knows React?"
                </span>
              )}
            </div>
            {/* "X new candidates match" hint chip — appears when the
                BE detects more matches for the active chain than the
                cached working set has. Click re-runs the chain and
                grows the set; chip disappears on success. Distinct
                amber tint so the user can tell it apart from the green
                action buttons and blue follow-up suggestions. */}
            {Number.isInteger(newCandidatesCount) && newCandidatesCount > 0 && (
              <div className="flex gap-2 flex-wrap mb-2">
                <button
                  type="button"
                  onClick={() => {
                    if (typeof onRefreshFilter === 'function') onRefreshFilter();
                  }}
                  title="Re-run your filter against the latest candidates"
                  className="chat_font_medium chat_transition"
                  style={{
                    padding: '8px 14px',
                    borderRadius: '8px',
                    border: `1px solid ${isDark ? '#92400E' : '#F59E0B'}`,
                    backgroundColor: isDark ? '#78350F' : '#FFFBEB',
                    color: isDark ? '#FCD34D' : '#92400E',
                    fontSize: '13px',
                    lineHeight: '18px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  ↻ {newCandidatesCount} new candidate{newCandidatesCount === 1 ? '' : 's'} match — include
                </button>
              </div>
            )}
            {/* Action buttons (handoff to Agent mode) */}
            {Array.isArray(filterActions) && filterActions.length > 0 && (
              <div className="flex gap-2 flex-wrap">
                {filterActions.map((action) => {
                  // Defensive: backend may emit `action`, `id`, or neither.
                  // Strip the `handoff_` prefix only if present; otherwise
                  // fall back to id / a stable string so the button
                  // never crashes on a missing field.
                  const rawAction = action?.action || action?.id || '';
                  const handoffKind = typeof rawAction === 'string'
                    ? rawAction.replace('handoff_', '')
                    : '';
                  const actionLabel = action?.label || 'Action';
                  const isOutreachAction = /outreach/i.test(actionLabel) || /outreach/i.test(rawAction);
                  return (
                  <button
                    key={rawAction || action?.label || Math.random()}
                    type="button"
                    onClick={() => handoffKind && onFilterAction && onFilterAction(handoffKind)}
                    title={action?.preview || ''}
                    className="chat_font_medium chat_transition"
                    style={{
                      padding: isOutreachAction ? '5px 10px' : '8px 16px',
                      borderRadius: isOutreachAction ? '6px' : '8px',
                      border: `1px solid ${isDark ? '#065F46' : '#059669'}`,
                      backgroundColor: isDark ? '#064E3B' : '#ECFDF5',
                      color: isDark ? '#6EE7B7' : '#047857',
                      fontSize: isOutreachAction ? '12px' : '13px',
                      lineHeight: isOutreachAction ? '16px' : '18px',
                      fontWeight: 500,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {actionLabel}
                  </button>
                  );
                })}
              </div>
            )}
            {/* Follow-up suggestion chips — Ask-mode hint chips that
                send their label as a fresh user message on click. Distinct
                visual style from the green action buttons (subtler, blue tint)
                so users can tell "suggestion" apart from "action". */}
            {Array.isArray(followUpSuggestions) && followUpSuggestions.length > 0 && (
              <div className="flex gap-2 flex-wrap mt-2">
                {followUpSuggestions.map((chip, idx) => {
                  const label = chip?.label || '';
                  if (!label) return null;
                  return (
                    <button
                      key={`followup_${idx}_${label}`}
                      type="button"
                      onClick={() => {
                        // Route the chip click through the same path a
                        // typed message takes — handleSendMessage inserts
                        // the optimistic user bubble, clears the input,
                        // then calls onUserQuery (which is what the
                        // parent's handleUserQueryForJD is). This way the
                        // user SEES their message land in the chat as if
                        // they typed it, rather than it happening silently
                        // in the background.
                        console.warn('[QFTRACE:chip_click]', label, {
                          onUserQueryType: typeof onUserQuery,
                          chatMode,
                        });
                        handleSendMessage(label, 'ask');
                      }}
                      title={chip?.kind ? `Suggestion (${chip.kind})` : 'Suggestion'}
                      className="chat_font_medium chat_transition"
                      style={{
                        padding: '6px 12px',
                        borderRadius: '16px',
                        border: `1px solid ${isDark ? '#1E3A8A' : '#BFDBFE'}`,
                        backgroundColor: isDark ? '#1E293B' : '#EFF6FF',
                        color: isDark ? '#93C5FD' : '#1D4ED8',
                        fontSize: '12px',
                        lineHeight: '16px',
                        fontWeight: 500,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      💡 {label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
        {/* Agent-mode context actions — hidden in Ask mode (filter actions shown instead) */}
        {chatMode !== 'ask' && ((Array.isArray(contextActions) && contextActions.length > 0) || scorecardEditorAvailable) ? (
          <div className="mb-3 flex min-w-0 items-center gap-2">
          <div
            className="flex min-w-0 flex-1 gap-2"
            style={{
              overflowX: 'auto',
              overflowY: 'hidden',
              whiteSpace: 'nowrap',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch',
              paddingBottom: '2px',
            }}
          >
            {(Array.isArray(contextActions) ? contextActions : []).map((action) => (
              <button
                key={action.id}
                type="button"
                onClick={() => {
                  const cmd = action?.command || action?.label || '';
                  if (!cmd) return;
                  if (chatMode === 'ask') {
                    // Option C: Block action in Ask mode — tell user to switch
                    setMessages((prev) => [
                      ...prev,
                      {
                        id: prev.length + 1,
                        sender: 'agent',
                        message: `To execute **"${cmd}"**, please switch to **Agent mode**. Pipeline mode is read-only and cannot perform actions.`,
                        timestamp: new Date().toISOString(),
                        mode: 'ask',
                      },
                    ]);
                    return;
                  }
                  handleSendMessage(cmd, 'agent');
                }}
                className="chat_font_medium chat_transition"
                style={{
                  padding: '6px 14px',
                  borderRadius: '999px',
                  border: `1px solid ${isDark ? '#4B5563' : '#D1D5DB'}`,
                  backgroundColor: isDark ? '#1F2937' : '#F9FAFB',
                  color: isDark ? '#F3F4F6' : '#111827',
                  fontSize: '12px',
                  lineHeight: '16px',
                  flexShrink: 0,
                  cursor: 'pointer',
                }}
              >
                {action.label}
              </button>
            ))}
          </div>
          {scorecardEditorAvailable && <button type="button" onClick={onOpenScorecardEditor}
            title="Edit assessment topics and weights" className="sticky right-0 flex shrink-0 items-center gap-1 rounded border border-indigo-300 bg-white px-3 py-1.5 text-xs font-medium text-indigo-700 shadow-sm dark:border-indigo-600 dark:bg-gray-900 dark:text-indigo-300">
            <FiSliders aria-hidden="true" /> Show UI
          </button>}
          {scorecardSavedSummary && <span className="sr-only" role="status">{scorecardSavedSummary}</span>}
          </div>
        ) : null}
        <ChatInput
          onSend={handleSendMessage}
          placeholder="Describe your ideal candidate or ask me anything..."
          value={inputMessage}
          onChange={setInputMessage}
          inputRef={inputRef}
          mode={chatMode}
          onModeChange={onChatModeChange}
          onAttachmentClick={onAttachmentClick}
          onUploadJD={openJdUploadModal}
          hasJd={hasJd}
          jdChecking={jdChecking}
          showFilterMode={showFilterMode}
        />
      </div>

      {/* Hidden file input for JD upload */}
      <input
        ref={jdFileInputRef}
        type="file"
        accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        style={{ display: 'none' }}
        onChange={handleJdFileSelected}
      />

      {/* JD Upload Modal */}
      {jdUploadModal.isOpen && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => setJdUploadModal(prev => ({ ...prev, isOpen: false }))}
        >
          <div
            className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-lg mx-4 flex flex-col overflow-hidden border border-gray-200/50 dark:border-gray-700/50"
            onClick={e => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between bg-gradient-to-r from-gray-50 to-white dark:from-gray-800/80 dark:to-gray-900">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#3B5BDB,#20C997)' }}>
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900 dark:text-white">Upload Job Description</h3>
                  <p className="text-xs text-gray-400 dark:text-gray-500">PDF or Word document</p>
                </div>
              </div>
              <button
                onClick={() => setJdUploadModal(prev => ({ ...prev, isOpen: false }))}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal body */}
            <div className="px-6 py-5">

              {/* STEP: pick */}
              {jdUploadModal.step === 'pick' && (
                <div className="flex flex-col items-center gap-4">
                  {jdUploadModal.error && (
                    <div className="w-full px-4 py-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm border border-red-200 dark:border-red-700">
                      {jdUploadModal.error}
                    </div>
                  )}
                  <div
                    className="w-full border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl py-10 flex flex-col items-center gap-3 cursor-pointer hover:border-blue-400 dark:hover:border-blue-500 transition-colors"
                    onClick={() => jdFileInputRef.current?.click()}
                  >
                    <svg className="w-10 h-10 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Click to select a PDF or Word file</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500">Max 5 MB &nbsp;·&nbsp; .pdf / .doc / .docx</p>
                  </div>
                </div>
              )}

              {/* STEP: uploading */}
              {jdUploadModal.step === 'uploading' && (
                <div className="flex flex-col items-center gap-4 py-6">
                  <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  <p className="text-sm text-gray-600 dark:text-gray-400">Extracting and analysing JD…</p>
                </div>
              )}

              {/* STEP: filling — ask for missing fields one at a time */}
              {jdUploadModal.step === 'filling' && (() => {
                const field = jdUploadModal.missingFields[jdUploadModal.fieldIndex];
                if (!field) return null;
                return (
                  <div className="flex flex-col gap-4">
                    <div className="px-4 py-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700">
                      <p className="text-xs text-blue-500 dark:text-blue-400 font-semibold uppercase tracking-wide mb-1">
                        Missing field {jdUploadModal.fieldIndex + 1} of {jdUploadModal.missingFields.length}
                      </p>
                      <p className="text-sm text-gray-800 dark:text-gray-200 font-medium">{field.question}</p>
                    </div>
                    <textarea
                      className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm px-4 py-3 outline-none focus:ring-2 focus:ring-blue-400 resize-none"
                      rows={3}
                      placeholder={`Enter ${field.label.toLowerCase()}…`}
                      value={jdUploadModal.fieldValues[field.key] || ''}
                      onChange={e =>
                        setJdUploadModal(prev => ({
                          ...prev,
                          fieldValues: { ...prev.fieldValues, [field.key]: e.target.value },
                        }))
                      }
                    />
                    <div className="flex gap-2 justify-end">
                      <button
                        type="button"
                        onClick={handleJdFieldNext}
                        className="px-4 py-2 rounded-lg text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        Skip
                      </button>
                      <button
                        type="button"
                        onClick={handleJdFieldNext}
                        disabled={!jdUploadModal.fieldValues[field.key]?.trim()}
                        className="px-5 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-40 transition-colors"
                        style={{ background: 'linear-gradient(135deg,#3B5BDB,#20C997)' }}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                );
              })()}

              {/* STEP: review */}
              {jdUploadModal.step === 'review' && (
                <div className="flex flex-col gap-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    JD extracted successfully ({jdUploadModal.jdText.length} characters). Review below and click <strong>Create JD</strong> to proceed.
                  </p>
                  <textarea
                    className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-xs px-4 py-3 outline-none resize-none font-mono"
                    rows={10}
                    value={jdUploadModal.jdText}
                    onChange={e => setJdUploadModal(prev => ({ ...prev, jdText: e.target.value }))}
                  />
                  <div className="flex gap-2 justify-end">
                    <button
                      type="button"
                      onClick={() => setJdUploadModal(prev => ({ ...prev, isOpen: false }))}
                      className="px-4 py-2 rounded-lg text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleJdSubmit}
                      className="px-5 py-2 rounded-lg text-sm font-semibold text-white transition-colors"
                      style={{ background: 'linear-gradient(135deg,#3B5BDB,#20C997)' }}
                    >
                      Create JD
                    </button>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      )}

      {/* Meeting Analysis Modal — replaced by the shared <MeetingAnalysisModal/> mounted below. */}
      {false && meetingAnalysisModal.isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setMeetingAnalysisModal({ isOpen: false, loading: false, candidate: null, analysis: null, error: null })}>
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-4xl mx-4 max-h-[85vh] flex flex-col overflow-hidden border border-gray-200/50 dark:border-gray-700/50" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between flex-shrink-0 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800/80 dark:to-gray-900">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white tracking-tight">Meeting Analysis</h3>
                  <p className="text-xs text-gray-400 dark:text-gray-500">{meetingAnalysisModal.candidate?.name || 'Candidate'}</p>
                </div>
              </div>
              <button onClick={() => setMeetingAnalysisModal({ isOpen: false, loading: false, candidate: null, analysis: null, error: null })} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                <svg className="w-5 h-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            {/* Body */}
            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5 bg-gray-50/50 dark:bg-gray-900/50">
              {meetingAnalysisModal.loading && (
                <div className="flex flex-col items-center justify-center py-24 gap-4">
                  <div className="w-12 h-12 rounded-full animate-spin" style={{ border: `3px solid ${primaryColor}20`, borderTopColor: primaryColor }} />
                  <p className="text-sm text-gray-400 font-medium">Loading meeting analysis...</p>
                </div>
              )}

              {meetingAnalysisModal.error && (
                <div className="text-center py-20">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-yellow-50 dark:bg-yellow-900/20 flex items-center justify-center">
                    <svg className="w-6 h-6 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01" /></svg>
                  </div>
                  <p className="text-sm text-yellow-600 dark:text-yellow-400 font-medium">{meetingAnalysisModal.error}</p>
                </div>
              )}

              {meetingAnalysisModal.analysis && (() => {
                console.log('[Modal] Rendering with analysis:', meetingAnalysisModal.analysis);
                const analysis = meetingAnalysisModal.analysis || {};
                const summary = analysis.summary || analysis.analysis_summary || analysis.overall_summary || '';
                const recommendation = analysis.recommendation || analysis.decision || '';
                const strengths = Array.isArray(analysis.strengths) ? analysis.strengths : Array.isArray(analysis.pros) ? analysis.pros : [];
                const concerns = Array.isArray(analysis.concerns) ? analysis.concerns : Array.isArray(analysis.risks) ? analysis.risks : [];
                const keyPoints = Array.isArray(analysis.key_points) ? analysis.key_points : Array.isArray(analysis.highlights) ? analysis.highlights : [];

                console.log('[Modal] Analysis data:', { success: analysis.success, status: analysis.status, hasSummary: !!summary, hasRecommendation: !!recommendation, strengthsCount: strengths.length, concernsCount: concerns.length });

                
                console.log('[Modal] Showing successful analysis state');
                // Handle successful analysis

                return (
                  <div className="space-y-6">
                    {/* Skills Matching Score */}
                    {analysis.skills_matching_score !== undefined && (
                      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Skills Matching Score</h4>
                        <div className="flex items-center gap-4">
                          <div className="relative w-16 h-16 flex-shrink-0">
                            <svg className="transform -rotate-90 w-16 h-16">
                              <circle cx="32" cy="32" r="28" stroke="currentColor" strokeWidth="4" fill="none" className="text-gray-200 dark:text-gray-700" />
                              <circle
                                cx="32"
                                cy="32"
                                r="28"
                                stroke="currentColor"
                                strokeWidth="4"
                                fill="none"
                                className="text-blue-600 dark:text-blue-400"
                                strokeDasharray={`${2 * Math.PI * 28}`}
                                strokeDashoffset={`${2 * Math.PI * 28 * (1 - (analysis.skills_matching_score || 0) / 10)}`}
                              />
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <span className="text-lg font-bold text-blue-700 dark:text-blue-300">{analysis.skills_matching_score?.toFixed(1) || '0'}</span>
                            </div>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`inline-flex px-3 py-1 rounded-full text-sm font-semibold ${
                                (analysis.skills_matching_score || 0) >= 8 ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                                (analysis.skills_matching_score || 0) >= 6 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' :
                                'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                              }`}>
                                {(analysis.skills_matching_score || 0) >= 8 ? 'Excellent Match' :
                                 (analysis.skills_matching_score || 0) >= 6 ? 'Good Match' : 'Poor Match'}
                              </span>
                            </div>
                            {summary && (
                              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{summary}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Recommendation */}
                    {recommendation && (
                      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Recommendation</h4>
                        <div className="flex items-center gap-3">
                          <span className={`inline-flex px-4 py-2 rounded-full text-sm font-semibold ${
                            recommendation === 'Hire' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                            recommendation === 'No Hire' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' :
                            'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                          }`}>
                            {recommendation === 'Maybe' ? 'Further Evaluation Needed' : recommendation}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Strengths */}
                    {strengths.length > 0 && (
                      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Strengths</h4>
                        <ul className="space-y-2">
                          {strengths.map((item, index) => (
                            <li key={index} className="flex items-start gap-2">
                              <span className="text-green-500 mt-0.5">â¢</span>
                              <span className="text-sm text-gray-700 dark:text-gray-300">{typeof item === 'string' ? item : JSON.stringify(item)}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Concerns */}
                    {concerns.length > 0 && (
                      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Concerns</h4>
                        <ul className="space-y-2">
                          {concerns.map((item, index) => (
                            <li key={index} className="flex items-start gap-2">
                              <span className="text-red-500 mt-0.5">â¢</span>
                              <span className="text-sm text-gray-700 dark:text-gray-300">{typeof item === 'string' ? item : JSON.stringify(item)}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Key Points */}
                    {keyPoints.length > 0 && (
                      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Key Points</h4>
                        <ul className="space-y-2">
                          {keyPoints.map((item, index) => (
                            <li key={index} className="flex items-start gap-2">
                              <span className="text-blue-500 mt-0.5">â¢</span>
                              <span className="text-sm text-gray-700 dark:text-gray-300">{typeof item === 'string' ? item : JSON.stringify(item)}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Meeting Notes */}
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Meeting Notes</h4>
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 max-h-60 overflow-y-auto">
                        <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                          {meetingAnalysisModal.candidate?.interview_notes || meetingAnalysisModal.candidate?.meeting_transcript || 'No meeting notes available yet.'}
                        </p>
                      </div>
                    </div>

                    {/* Video Section */}
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Interview Video</h4>
                      <div className="flex flex-col items-center">
                        {meetingAnalysisModal.candidate?.video_url ? (
                          <>
                            <video 
                              className="w-full max-w-2xl rounded-lg shadow-lg"
                              controls
                              src={meetingAnalysisModal.candidate.video_url}
                            >
                              Your browser does not support the video tag.
                            </video>
                            <a 
                              href={meetingAnalysisModal.candidate.video_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all hover:shadow-md border-2"
                              style={{ borderColor: primaryColor, color: primaryColor, backgroundColor: 'transparent' }}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 21.634l-3.5-6.064A9 9 0 111.616 0 0l-6.064 6.064A9 9 0 00-12.736 0 0l6.064 6.064a9 9 0 0012.736 0 0l3.5 6.064a9 9 0 00-6.064 6.064A9 9 0 0111.616 0 0l6.064-6.064a9 9 0 00-6.064 6.064z" />
                              </svg>
                              Open Video in New Tab
                            </a>
                          </>
                        ) : (
                          <div className="text-center py-8">
                            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                              <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.5 4.5M15 14l4.5 4.5M15 18m0 6L4.5 18M9 6" />
                              </svg>
                            </div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              No interview video available
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Shared interview result / meeting analysis modal */}
      <MeetingAnalysisModal
        open={meetingAnalysisModal.isOpen}
        onClose={() => setMeetingAnalysisModal({ isOpen: false, loading: false, candidate: null, analysis: null, aiResult: null, error: null })}
        candidate={meetingAnalysisModal.candidate}
        aiResult={meetingAnalysisModal.aiResult}
        analysis={meetingAnalysisModal.analysis}
        loading={meetingAnalysisModal.loading}
        error={meetingAnalysisModal.error}
        onReject={() => setRejectCandidate(meetingAnalysisModal.candidate)}
      />

      <RejectCandidateModal
        open={!!rejectCandidate}
        candidate={rejectCandidate}
        searchId={rejectCandidate?._searchId || rejectCandidate?.search_id || rejectCandidate?.searchId || syncSearchId || ''}
        sessionId={rejectCandidate?.session_id || rejectCandidate?.sessionId || selectedJobId || ''}
        onClose={() => setRejectCandidate(null)}
        onRejected={() => {
          const rc = rejectCandidate;
          if (rc) {
            setRejectedKeys((prev) => {
              const next = new Set(prev);
              const id = rc.id ?? rc.candidate_id;
              if (id != null) next.add(`id:${id}`);
              const em = String(rc.email || rc.candidate_email || '').toLowerCase();
              if (em) next.add(`email:${em}`);
              return next;
            });
          }
          onCandidateRejected?.(rc);
          setRejectCandidate(null);
          setMeetingAnalysisModal({ isOpen: false, loading: false, candidate: null, analysis: null, aiResult: null, error: null });
        }}
      />
    </div>
  );
};

export default ChatMain;
